import React, {
	FC,
	useEffect,
	useRef,
	useState,
	useMemo,
	memo,
	ReactNode,
	isValidElement,
} from 'react';
import {
	Box,
	Image,
	List,
	ListItem,
	Skeleton,
	Text,
	Link as ChakraLink,
	Button,
	Flex,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	VStack,
	Badge,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useNavigate } from 'react-router-dom';
import CatalogPreview from './CatalogPreview';
import CatalogSelectPreview from './CatalogSelectPreview';
import LocationCard from 'src/components/common/LocationCard';
import { useRealtimeChatContext } from '../hooks/useRealtimeChatContext';
import CustomerProfileCard from 'src/components/common/CustomerProfileCard';
import { useCatalogsContext } from 'src/contexts/catalogs/CatalogsContext';
import { useChatData } from '../contexts/ChatDataContext';
import useCopilotChatContext from '../hooks/useCopilotChatContext';
import { ChannelSelector, ChannelOptionsGrid } from 'src/components/common/ChannelSelector';
import type { ChannelType } from '../contexts/ChatDataContext';
import config from 'src/config';
import { useRightSidebarContext } from 'src/contexts/app/RightSidebarContext';
import { useOnboarding } from 'src/contexts/onboarding';
// Website creation components
import WebsiteOffer from './WebsiteOffer';
import DomainSelector from './DomainSelector';
import DomainConfirmation from './DomainConfirmation';
import WebsitePreview from './WebsitePreview';
import LogoCompletion from './LogoCompletion';
import VoiceAgentCompletion from './VoiceAgentCompletion';
// Design Direction components
import DDPreviewCard from './DDPreviewCard';
import DDOptionsGrid from './DDOptionsGrid';
// Video Ad components
import VideoAdGenerating from './VideoAdGenerating';
import VideoAdCatalogList, { VideoAdCatalogItem } from './VideoAdCatalogList';

interface CustomWindow extends Window {
	fusion?: any;
}
declare const window: CustomWindow;

interface IMessageProps {
	content: string;
	onImageClick?: (src: string) => void;
	onContentResize?: () => void;
}

interface NodeProps {
	node: {
		properties?: {
			[key: string]: any;
		};
	};
	children?: ReactNode;
}

const preprocessContent = (content: string) => {
	try {
		// Removed excessive logging to reduce console noise
		// if (content.includes('catalog-preview') || content.includes('product')) {
		// 	console.log('📦 [Message] preprocessContent - Content with catalog/product:', {
		// 		contentPreview: content.substring(0, 500),
		// 		hasCatalogPreview: content.includes('catalog-preview'),
		// 		hasProduct: content.includes('product')
		// 	});
		// }

		// CRITICAL FIX: Decode HTML entities first
		// Messages loaded from database have HTML-encoded tags like &lt;div&gt;
		// We need to decode them before processing
		// Use textarea instead of div because textarea decodes entities properly
		const tempElement = document.createElement('textarea');
		tempElement.innerHTML = content;
		let processed = tempElement.value || content;

		// Only log if HTML entities were actually decoded
		// const hadEntities = content.includes('&lt;') || content.includes('&gt;');
		// if (hadEntities) {
		// 	console.log('📝 Decoded HTML entities:', {
		// 		originalLength: content.length,
		// 		decodedLength: processed.length,
		// 		preview: processed.substring(0, 200),
		// 	});
		// }

		// Remove any leading/trailing whitespace from each line while preserving structure
		processed = processed.split('\n').map(line => line.trim()).join('\n');

		// Remove internal IDs from user messages (e.g., [designDirectionId:xxx])
		// These are used by the backend but shouldn't be visible to users
		processed = processed.replace(/\s*\[designDirectionId:[^\]]+\]/g, '');

		// Remove leading whitespace from hyphenated HTML tags
		processed = processed.replace(
			/^[ \t]+(<[a-z0-9]+-[^>]*\/?>)/gims,
			'$1',
		);


		// CRITICAL FIX: Remove newlines and whitespace from inside HTML div blocks
		// This prevents ReactMarkdown from treating HTML with newlines as code blocks
		// Convert: <div>\n<tag />\n</div> → <div><tag /></div>
		processed = processed.replace(
			/<div>([\s\S]*?)<\/div>/g,
			(match, innerContent) => {
				// Remove all newlines and excessive whitespace from inside the div
				const cleaned = innerContent.replace(/\s*\n\s*/g, '');
				return `<div>${cleaned}</div>`;
			}
		);

		// CRITICAL FIX #2: Ensure blank lines between HTML blocks
		// ReactMarkdown requires blank lines to properly parse HTML blocks
		// Convert: </div>\n<div> → </div>\n\n<div>
		processed = processed.replace(
			/<\/div>\s*\n\s*<div>/g,
			'</div>\n\n<div>'
		);

		// Also ensure blank line before first div if preceded by text
		processed = processed.replace(
			/([^\n])\n<div>/g,
			'$1\n\n<div>'
		);

		// Ensure blank line after last div if followed by text
		processed = processed.replace(
			/<\/div>\n([^\n])/g,
			'</div>\n\n$1'
		);


		// CRITICAL FIX: Convert self-closing custom tags to open/close tags
		// rehype-raw doesn't properly parse self-closing tags for non-void elements
		// Convert: <service-selection-card ... /> → <service-selection-card ...></service-selection-card>
		const selfClosingCustomTags = [
			'service-selection-card',
			'suggestion-option',
			'industry-option',
			'bordered-container',
			'product-suggestion',
			'website-preview',
			'logo-completion',
			'voice-agent-completion',
			'campaign-success',
			'dd-preview',
			'dd-options',
			'dd-add-button',
			'video-ad-generating',
			'video-ad-catalog-item',
			'video-ad-create-catalog',
		];
		selfClosingCustomTags.forEach(tagName => {
			const selfClosingRegex = new RegExp(
				`<(${tagName})([^>]*?)\\s*/>`,
				'gi'
			);
			processed = processed.replace(selfClosingRegex, '<$1$2></$1>');
		});

		// FIX: Separate consecutive custom tags (e.g., "></service-selection-card><service-selection-card" → "></service-selection-card>\n<service-selection-card")
		processed = processed.replace(
			/(<\/[a-z]+-[a-z-]+>)\s*(<[a-z]+-[a-z-]+)/gi,
			'$1\n$2'
		);

		// FIX: Add whitespace after container opening tags (e.g., "<service-selection-container><service-selection-card" → "<service-selection-container>\n<service-selection-card")
		// This ensures child elements are properly parsed as children, not as text content
		processed = processed.replace(
			/(<(?:service-selection-container|suggestion-options|industry-options|video-ad-catalog-list)[^>]*>)\s*(<[a-z]+-[a-z-]+)/gi,
			'$1\n$2'
		);

		// FIX: Add whitespace before container closing tags
		processed = processed.replace(
			/(<\/[a-z]+-[a-z-]+>)\s*(<\/(?:service-selection-container|suggestion-options|industry-options|video-ad-catalog-list)>)/gi,
			'$1\n$2'
		);

		// DEBUG: Log if we have service-selection-container to see what's being processed
		if (processed.includes('service-selection-container')) {
			console.log('🔍 [Message] service-selection-container content:', {
				content: processed,
				cardCount: (processed.match(/<service-selection-card/g) || []).length,
			});
		}

		const customTagsWithAttrs = [
			'small-logo',
			'generated-logo',
			'regenerated-logo',
			'domain-selector',
			'domain-confirmation',
			'website-preview',
			'logo-completion',
			'voice-agent-completion',
			'campaign-success',
			'bordered-container',
			'product-suggestion',
			'suggestion-option',
			'suggestion-options',
			'service-selection-card',
			'service-selection-container',
			'industry-options',
			'industry-option',
			'dd-preview',
			'dd-options',
			'dd-add-button',
			'video-ad-generating',
			'video-ad-catalog-list',
			'video-ad-catalog-item',
			'video-ad-create-catalog',
		];

		customTagsWithAttrs.forEach(tagName => {
			// Match the tag with any attributes (self-closing or with closing tag)
			const tagRegex = new RegExp(
				`(<${tagName}[^>]*>(?:[\\s\\S]*?<\\/${tagName}>)?)`,
				'gi'
			);
			processed = processed.replace(tagRegex, '\n\n$1\n\n');
		});

		// GENERIC FIX: Unwrap ANY hyphenated custom tags from divs
		// ReactMarkdown/rehypeRaw handles custom tags better when they're not wrapped
		// Convert: <div><any-custom-tag ... /></div> → <any-custom-tag ... />
		let unwrapCount = 0;
		const unwrappedTags: string[] = [];
		const unwrappedDetails: Array<{ tagName: string; content: string }> = [];

		processed = processed.replace(
			/<div>\s*(<[a-z]+-[a-z-]+[^>]*\/?>(?:[\s\S]*?<\/[a-z]+-[a-z-]+>)?)\s*<\/div>/gi,
			(match, tagContent) => {
				unwrapCount++;
				// Extract tag name for logging
				const tagNameMatch = tagContent.match(/<([a-z]+-[a-z-]+)/i);
				if (tagNameMatch) {
					const tagName = tagNameMatch[1];
					unwrappedTags.push(tagName);
					// Only store preview content for location-preview and service-area-preview
					if (tagName === 'location-preview' || tagName === 'service-area-preview') {
						unwrappedDetails.push({
							tagName,
							content: tagContent.substring(0, 150),
						});
					}
				}
				// Return tag with blank lines around it for proper parsing
				return `\n\n${tagContent}\n\n`;
			}
		);

		// Only log if custom tags were unwrapped
		// if (unwrapCount > 0) {
		// 	console.log('📝 Unwrapped custom tags:', {
		// 		count: unwrapCount,
		// 		tags: unwrappedTags,
		// 	});
		// 	// Log details for preview tags
		// 	if (unwrappedDetails.length > 0) {
		// 		console.log('📍 Preview tags found:', unwrappedDetails);
		// 	}
		// }

		// Log channel-option tags for debugging
		if (processed.includes('<channel-option')) {
			console.log('🔧 [preprocessContent] channel-option tags found:', {
				content: processed,
				channelMatches: processed.match(/<channel-option[^>]*>.*?<\/channel-option>/g) || processed.match(/<channel-option[^>]*\/>/g),
			});
		}

		// Remove trailing newlines to prevent empty paragraphs at the end
		processed = processed.replace(/\n+$/, '');

		// Remove multiple consecutive newlines (more than 2) to prevent excessive spacing
		processed = processed.replace(/\n{3,}/g, '\n\n');

		return processed;
	} catch (e) {
		console.warn('❌ Preprocessor regex failed, returning original content:', e);
		return content;
	}
};

/**
 * Handles campaign navigation with forced sections configuration.
 * Automatically sets the first two sections as open and the first section as active.
 * Includes flag to prevent automatic scroll when coming from chat.
 *
 * @param url - The URL to navigate to
 */
const handleCampaignNavigation = (url: string) => {
	// Check if it's a campaign URL
	const campaignMatch = url.match(/\/projects\/campaigns\/([^?]+)(\?.*)?$/);
	if (campaignMatch) {
		// Always set only step 2 open and active when coming from copilot
		const sections = [false, true, false, false];
		const activeSection = 1;

		// Send navigation event before navigating
		const navigationEvent = {
			target: 'campaign',
			action: 'setNavigation',
			value: {
				sections,
				activeSection,
				preventScroll: true, // Prevent automatic scroll when coming from chat
			},
		};

		// Ensure window.fusion exists and push the event
		if (!window.fusion) {
			window.fusion = [];
		}
		if (Array.isArray(window.fusion)) {
			window.fusion.push(navigationEvent);
		} else if (window.fusion.push) {
			window.fusion.push(navigationEvent);
		}
	}
};

const loadedImagesCache = new Set<string>();

// CatalogPreviewWrapper component - for display/editing only, no selection
const CatalogPreviewWrapper = ({ catalogId }: { catalogId: string }) => {
	const { getCatalogById, isLoading, refetch } = useCatalogsContext();
	const catalog = getCatalogById(catalogId);
	const [isLocalDeleted, setIsLocalDeleted] = useState(false);
	const [hasTriedRefetch, setHasTriedRefetch] = useState(false);

	// Listen for custom events when catalog is deleted
	useEffect(() => {
		const handleCatalogDeleted = (event: CustomEvent) => {
			if (event.detail?.catalogId === catalogId) {
				setIsLocalDeleted(true);
			}
		};

		window.addEventListener(
			'catalog-deleted',
			handleCatalogDeleted as EventListener,
		);

		return () => {
			window.removeEventListener(
				'catalog-deleted',
				handleCatalogDeleted as EventListener,
			);
		};
	}, [catalogId]);

	// If catalog not found and we haven't tried refetching, try once
	useEffect(() => {
		if (!catalog && !isLoading && !hasTriedRefetch && catalogId) {
			console.log('[CatalogPreviewWrapper] Catalog not found, triggering refetch:', catalogId);
			setHasTriedRefetch(true);
			refetch();
		}
	}, [catalog, isLoading, hasTriedRefetch, catalogId, refetch]);

	// Don't render wrapper if catalog was deleted
	if (isLocalDeleted) {
		return null;
	}

	// Show loading state if catalog not found yet
	if (!catalog) {
		if (isLoading) {
			return (
				<Box w="100%" maxW="650px" p={4} borderRadius="xl" bg="gray.50">
					<Skeleton height="100px" borderRadius="lg" />
				</Box>
			);
		}
		// Catalog still not found after loading - log for debugging
		console.log('[CatalogPreviewWrapper] Catalog not found after load:', catalogId);
		return null;
	}

	return (
		<Box
			w="100%"
			maxW="650px"
			borderRadius="xl"
			boxShadow="0 0 4px rgba(0, 0, 0, 0.1)"
			_hover={{
				boxShadow: 'lg',
			}}
			transition="all 0.2s ease-in-out"
		>
			<CatalogPreview key={catalogId} catalogId={catalogId} />
		</Box>
	);
};

CatalogPreviewWrapper.displayName = 'CatalogPreviewWrapper';

// Logo Options Component - shows upload and generate buttons
const LogoOptionsComponent: FC = () => {
	const { sendMessage } = useRealtimeChatContext();
	const [isGenerating, setIsGenerating] = useState(false);
	const [selectedOption, setSelectedOption] = useState<'upload' | 'generate' | null>(null);

	const handleUpload = () => {
		setSelectedOption('upload');
		sendMessage('I want to upload my own logo');
	};

	const handleGenerate = () => {
		setSelectedOption('generate');
		setIsGenerating(true);
		sendMessage('Generate logo options for me');
	};

	return (
		<Flex gap={3} flexWrap="wrap" mt={2}>
			<Button
				bg={selectedOption === 'upload' ? 'orange.base' : 'white'}
				color={selectedOption === 'upload' ? 'white' : 'orange.base'}
				borderWidth="2px"
				borderColor="orange.base"
				_hover={{ bg: selectedOption === 'upload' ? 'orange.primary' : 'orange.lighter' }}
				size="md"
				onClick={handleUpload}
				borderRadius="xl"
				isDisabled={selectedOption !== null}
				fontFamily="'Noto Sans', 'Open Sans', sans-serif"
				fontWeight="medium"
			>
				📤 Upload my logo
			</Button>
			<Button
				bg="orange.base"
				color="white"
				_hover={{ bg: 'orange.primary' }}
				size="md"
				onClick={handleGenerate}
				isLoading={isGenerating}
				loadingText="Generating..."
				borderRadius="xl"
				isDisabled={selectedOption !== null && selectedOption !== 'generate'}
				fontFamily="'Noto Sans', 'Open Sans', sans-serif"
				fontWeight="medium"
			>
				✨ Generate logo with AI
			</Button>
		</Flex>
	);
};

LogoOptionsComponent.displayName = 'LogoOptionsComponent';

// Account Logo Preview Component - shows existing logo from account
const AccountLogoPreviewComponent: FC<{ url: string }> = ({ url }) => {
	return (
		<Box
			display="flex"
			justifyContent="center"
			alignItems="center"
			p={4}
			my={3}
			borderRadius="xl"
			bg="gray.50"
			borderWidth="1px"
			borderColor="gray.200"
		>
			<Image
				src={url}
				alt="Your current logo"
				maxH="120px"
				maxW="200px"
				objectFit="contain"
				borderRadius="md"
			/>
		</Box>
	);
};

AccountLogoPreviewComponent.displayName = 'AccountLogoPreviewComponent';

// Account Logo Options Component - shows buttons for existing logo scenario
const AccountLogoOptionsComponent: FC = () => {
	const { sendMessage } = useRealtimeChatContext();
	const [selectedOption, setSelectedOption] = useState<'use' | 'upload' | 'generate' | null>(null);
	const [isGenerating, setIsGenerating] = useState(false);

	const handleUseThis = () => {
		setSelectedOption('use');
		sendMessage('Yes, use this logo');
	};

	const handleUpload = () => {
		setSelectedOption('upload');
		sendMessage('I want to upload a different logo');
	};

	const handleGenerate = () => {
		setSelectedOption('generate');
		setIsGenerating(true);
		sendMessage('Generate new logo options with AI');
	};

	return (
		<Flex gap={3} flexWrap="wrap" mt={2} justifyContent="center">
			<Button
				bg={selectedOption === 'use' ? 'green.500' : 'white'}
				color={selectedOption === 'use' ? 'white' : 'green.500'}
				borderWidth="2px"
				borderColor="green.500"
				_hover={{ bg: selectedOption === 'use' ? 'green.600' : 'green.50' }}
				size="md"
				onClick={handleUseThis}
				borderRadius="xl"
				isDisabled={selectedOption !== null}
				fontFamily="'Noto Sans', 'Open Sans', sans-serif"
				fontWeight="medium"
			>
				✓ Use this logo
			</Button>
			<Button
				bg={selectedOption === 'upload' ? 'orange.base' : 'white'}
				color={selectedOption === 'upload' ? 'white' : 'orange.base'}
				borderWidth="2px"
				borderColor="orange.base"
				_hover={{ bg: selectedOption === 'upload' ? 'orange.primary' : 'orange.lighter' }}
				size="md"
				onClick={handleUpload}
				borderRadius="xl"
				isDisabled={selectedOption !== null}
				fontFamily="'Noto Sans', 'Open Sans', sans-serif"
				fontWeight="medium"
			>
				📤 Upload my logo
			</Button>
			<Button
				bg={selectedOption === 'generate' ? 'orange.base' : 'orange.base'}
				color="white"
				_hover={{ bg: 'orange.primary' }}
				size="md"
				onClick={handleGenerate}
				isLoading={isGenerating}
				loadingText="Generating..."
				borderRadius="xl"
				isDisabled={selectedOption !== null && selectedOption !== 'generate'}
				fontFamily="'Noto Sans', 'Open Sans', sans-serif"
				fontWeight="medium"
			>
				✨ Generate with AI
			</Button>
		</Flex>
	);
};

AccountLogoOptionsComponent.displayName = 'AccountLogoOptionsComponent';

// Single Generated Logo Card Component
const GeneratedLogoCard: FC<{
	id: string;
	url: string;
	style: string;
	isSelected: boolean;
	onSelect: () => void;
	onPreview: () => void;
	onModify: () => void;
}> = ({ id, url, style, isSelected, onSelect, onPreview, onModify }) => {
	return (
		<Box
			borderRadius="xl"
			overflow="hidden"
			borderWidth={isSelected ? '3px' : '1px'}
			borderColor={isSelected ? 'orange.base' : 'gray.200'}
			bg="white"
			boxShadow={isSelected ? 'lg' : 'sm'}
			transition="all 0.2s"
			p={4}
			minW="200px"
			maxW="220px"
		>
			<VStack spacing={3}>
				<Box
					cursor="pointer"
					onClick={onPreview}
					_hover={{ opacity: 0.9 }}
					transition="opacity 0.2s"
				>
					<Image
						src={url}
						alt={`Generated logo - ${style}`}
						h="180px"
						w="180px"
						objectFit="contain"
						borderRadius="md"
					/>
				</Box>
				<Text
					fontSize="sm"
					color="gray.text"
					fontWeight="medium"
					textAlign="center"
					fontFamily="'Noto Sans', 'Open Sans', sans-serif"
				>
					{style}
				</Text>
				{isSelected ? (
					<Badge bg="orange.base" color="white" fontSize="sm" px={3} py={1}>
						✓ Selected
					</Badge>
				) : (
					<VStack spacing={2} w="100%">
						<Button
							size="sm"
							bg="orange.base"
							color="white"
							_hover={{ bg: 'orange.primary' }}
							w="100%"
							onClick={onSelect}
							fontFamily="'Noto Sans', 'Open Sans', sans-serif"
							fontWeight="medium"
						>
							Use this logo
						</Button>
						<Button
							size="sm"
							variant="outline"
							borderColor="gray.300"
							color="gray.600"
							_hover={{ bg: 'gray.50', borderColor: 'gray.400' }}
							w="100%"
							onClick={onModify}
							fontFamily="'Noto Sans', 'Open Sans', sans-serif"
							fontWeight="normal"
						>
							✏️ Modify
						</Button>
					</VStack>
				)}
			</VStack>
		</Box>
	);
};

GeneratedLogoCard.displayName = 'GeneratedLogoCard';

// Logo Preview Modal Component
const LogoPreviewModal: FC<{
	isOpen: boolean;
	onClose: () => void;
	logo: { id: string; url: string; style: string } | null;
	isSelected: boolean;
	onSelect: () => void;
	initialShowFeedback?: boolean;
}> = ({ isOpen, onClose, logo, isSelected, onSelect, initialShowFeedback = false }) => {
	const { sendMessage } = useRealtimeChatContext();
	const [showFeedback, setShowFeedback] = useState(initialShowFeedback);
	const [feedback, setFeedback] = useState('');
	const [isRegenerating, setIsRegenerating] = useState(false);

	// Update showFeedback when initialShowFeedback changes (e.g., when opening modal for modify)
	useEffect(() => {
		setShowFeedback(initialShowFeedback);
	}, [initialShowFeedback, isOpen]);

	if (!logo) return null;

	const handleSelect = () => {
		onSelect();
		onClose();
	};

	const handleRegenerateWithFeedback = () => {
		if (!feedback.trim()) return;
		setIsRegenerating(true);
		// Send message to regenerate this specific logo style with feedback
		sendMessage(`Regenerate the ${logo.style} logo with this feedback: ${feedback.trim()}`);
		setFeedback('');
		setShowFeedback(false);
		setIsRegenerating(false);
		onClose();
	};

	const handleClose = () => {
		setShowFeedback(false);
		setFeedback('');
		onClose();
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
			<ModalOverlay zIndex={20000} />
			<ModalContent borderRadius="xl" mx={4} containerProps={{ zIndex: 20001 }}>
				<ModalHeader
					fontFamily="'Noto Sans', 'Open Sans', sans-serif"
					borderBottomWidth="1px"
					borderColor="gray.100"
				>
					{logo.style}
				</ModalHeader>
				<ModalBody py={6}>
					<VStack spacing={4}>
						<Box
							bg="gray.50"
							borderRadius="lg"
							p={4}
							w="100%"
							display="flex"
							justifyContent="center"
						>
							<Image
								src={logo.url}
								alt={`Generated logo - ${logo.style}`}
								maxH="350px"
								maxW="100%"
								objectFit="contain"
							/>
						</Box>
						{showFeedback && (
							<Box w="100%">
								<Text
									fontSize="sm"
									fontWeight="medium"
									mb={2}
									color="gray.600"
									fontFamily="'Noto Sans', 'Open Sans', sans-serif"
								>
									What changes would you like?
								</Text>
								<textarea
									value={feedback}
									onChange={(e) => setFeedback(e.target.value)}
									placeholder="e.g., Change the colors to blue and gold, make the icon bigger, use a bolder font..."
									style={{
										width: '100%',
										minHeight: '80px',
										padding: '12px',
										borderRadius: '8px',
										border: '1px solid #E2E8F0',
										fontFamily: "'Noto Sans', 'Open Sans', sans-serif",
										fontSize: '14px',
										resize: 'vertical',
									}}
								/>
							</Box>
						)}
					</VStack>
				</ModalBody>
				<ModalFooter borderTopWidth="1px" borderColor="gray.100" gap={3} flexWrap="wrap">
					{showFeedback ? (
						<>
							<Button variant="ghost" onClick={() => setShowFeedback(false)}>
								Cancel
							</Button>
							<Button
								bg="orange.base"
								color="white"
								_hover={{ bg: 'orange.primary' }}
								onClick={handleRegenerateWithFeedback}
								isLoading={isRegenerating}
								isDisabled={!feedback.trim()}
							>
								Regenerate
							</Button>
						</>
					) : (
						<>
							<Button
								variant="outline"
								borderColor="orange.base"
								color="orange.base"
								_hover={{ bg: 'orange.lighter' }}
								onClick={() => setShowFeedback(true)}
								size="md"
							>
								✏️ Modify this style
							</Button>
							<Box flex="1" />
							<Button variant="ghost" onClick={handleClose}>
								Close
							</Button>
							{isSelected ? (
								<Button
									bg="orange.base"
									color="white"
									_hover={{ bg: 'orange.primary' }}
									isDisabled
									leftIcon={<Text>✓</Text>}
								>
									Selected
								</Button>
							) : (
								<Button
									bg="orange.base"
									color="white"
									_hover={{ bg: 'orange.primary' }}
									onClick={handleSelect}
								>
									Use this logo
								</Button>
							)}
						</>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

LogoPreviewModal.displayName = 'LogoPreviewModal';

// Retry Action Component - allows user to retry failed messages
const RetryActionComponent: FC = () => {
	const { retryLastMessage, lastFailedMessage } = useCopilotChatContext();

	if (!lastFailedMessage) {
		return null;
	}

	return (
		<Box mt={3}>
			<Button
				size="sm"
				colorScheme="orange"
				variant="outline"
				leftIcon={<Text>↻</Text>}
				onClick={retryLastMessage}
			>
				Retry
			</Button>
		</Box>
	);
};

RetryActionComponent.displayName = 'RetryActionComponent';

// Generated Logo Component - handles rendering all logos from chat context
const GeneratedLogoComponent: FC<{ id: string; url: string; style: string }> = ({
	id,
	url,
	style,
}) => {
	const { sendMessage } = useRealtimeChatContext();
	const { generatedLogos, pendingLogoCount, selectedLogoId, setSelectedLogoId } = useChatData();

	// Modal state for logo preview
	const [previewLogo, setPreviewLogo] = useState<{ id: string; url: string; style: string } | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [openWithFeedback, setOpenWithFeedback] = useState(false);

	// If there are multiple logos in context, render all of them (only once, from the first tag)
	const logoCount = generatedLogos?.length || 0;
	const isFirstTag = id === 'logo-1';

	console.log('[GeneratedLogoComponent] Rendering:', { id, logoCount, isFirstTag, selectedLogoId });

	const handleSelect = (logoId: string, logoUrl: string, style?: string) => {
		setSelectedLogoId(logoId);
		// Send a clear message that includes the URL so the AI can call select_logo with it
		const styleName = style || logoId;
		sendMessage(`I want to use this logo (${styleName}): ${logoUrl}`);
	};

	const handlePreview = (logo: { id: string; url: string; style: string }) => {
		setPreviewLogo(logo);
		setOpenWithFeedback(false);
		setIsPreviewOpen(true);
	};

	const handleModify = (logo: { id: string; url: string; style: string }) => {
		setPreviewLogo(logo);
		setOpenWithFeedback(true);
		setIsPreviewOpen(true);
	};

	const handleClosePreview = () => {
		setIsPreviewOpen(false);
		setPreviewLogo(null);
		setOpenWithFeedback(false);
	};

	// Only the first tag renders all logos to avoid duplication
	if (logoCount > 1 && !isFirstTag) {
		console.log('[GeneratedLogoComponent] Skipping render - not first tag');
		return null;
	}

	// Note: We no longer hide original logos when a regenerated logo is selected
	// Previous messages should remain visible as part of conversation history

	// If multiple logos in context (or pending), render them all in a grid
	if ((logoCount > 1 || pendingLogoCount > 0) && isFirstTag) {
		console.log('[GeneratedLogoComponent] Rendering', logoCount, 'logos from context +', pendingLogoCount, 'pending');
		return (
			<>
				<Flex gap={4} flexWrap="wrap" mt={2} justifyContent="flex-start">
					{generatedLogos.map((logo: { id: string; url: string; style: string }) => (
						<GeneratedLogoCard
							key={logo.id}
							id={logo.id}
							url={logo.url}
							style={logo.style}
							isSelected={selectedLogoId === logo.id}
							onSelect={() => handleSelect(logo.id, logo.url, logo.style)}
							onPreview={() => handlePreview(logo)}
							onModify={() => handleModify(logo)}
						/>
					))}
					{/* Skeleton placeholders for pending logos */}
					{pendingLogoCount > 0 && Array.from({ length: pendingLogoCount }).map((_, index) => (
						<Box
							key={`skeleton-${index}`}
							borderRadius="md"
							overflow="hidden"
							border="1px solid"
							borderColor="gray.200"
							bg="gray.50"
							p={2}
							width="200px"
						>
							<Skeleton height="150px" borderRadius="md" mb={2} />
							<Skeleton height="20px" width="80%" mb={2} />
							<Flex gap={2}>
								<Skeleton height="32px" flex={1} />
								<Skeleton height="32px" flex={1} />
							</Flex>
						</Box>
					))}
				</Flex>
				<LogoPreviewModal
					isOpen={isPreviewOpen}
					onClose={handleClosePreview}
					logo={previewLogo}
					isSelected={previewLogo ? selectedLogoId === previewLogo.id : false}
					onSelect={() => previewLogo && handleSelect(previewLogo.id, previewLogo.url, previewLogo.style)}
					initialShowFeedback={openWithFeedback}
				/>
			</>
		);
	}

	// Single logo fallback
	const singleLogo = { id, url, style };
	return (
		<>
			<Flex gap={4} flexWrap="wrap" mt={2} justifyContent="flex-start">
				<GeneratedLogoCard
					id={id}
					url={url}
					style={style}
					isSelected={selectedLogoId === id}
					onSelect={() => handleSelect(id, url, style)}
					onPreview={() => handlePreview(singleLogo)}
					onModify={() => handleModify(singleLogo)}
				/>
			</Flex>
			<LogoPreviewModal
				isOpen={isPreviewOpen}
				onClose={handleClosePreview}
				logo={previewLogo}
				isSelected={previewLogo ? selectedLogoId === previewLogo.id : false}
				onSelect={() => previewLogo && handleSelect(previewLogo.id, previewLogo.url, previewLogo.style)}
				initialShowFeedback={openWithFeedback}
			/>
		</>
	);
};

GeneratedLogoComponent.displayName = 'GeneratedLogoComponent';

// Regenerated Logo Component - handles rendering a single regenerated logo (after feedback modification)
const RegeneratedLogoComponent: FC<{ id: string; url: string; style: string }> = ({
	id,
	url,
	style,
}) => {
	const { sendMessage } = useRealtimeChatContext();
	const { selectedLogoId, setSelectedLogoId } = useChatData();

	// Modal state for logo preview
	const [previewLogo, setPreviewLogo] = useState<{ id: string; url: string; style: string } | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [openWithFeedback, setOpenWithFeedback] = useState(false);

	const handleSelect = () => {
		setSelectedLogoId(id);
		// Send a clear message that includes the URL so the AI can call select_logo with it
		sendMessage(`I want to use this logo (${style}): ${url}`);
	};

	const handlePreview = () => {
		setPreviewLogo({ id, url, style });
		setOpenWithFeedback(false);
		setIsPreviewOpen(true);
	};

	const handleModify = () => {
		setPreviewLogo({ id, url, style });
		setOpenWithFeedback(true);
		setIsPreviewOpen(true);
	};

	const handleClosePreview = () => {
		setIsPreviewOpen(false);
		setPreviewLogo(null);
		setOpenWithFeedback(false);
	};

	// If a different logo is selected, hide this regenerated logo
	if (selectedLogoId && selectedLogoId !== id) {
		console.log('[RegeneratedLogoComponent] Hiding - different logo selected:', selectedLogoId);
		return null;
	}

	// Render this regenerated logo (either unselected or selected)
	return (
		<>
			<Flex gap={4} flexWrap="wrap" mt={2} justifyContent="flex-start">
				<GeneratedLogoCard
					id={id}
					url={url}
					style={style}
					isSelected={selectedLogoId === id}
					onSelect={handleSelect}
					onPreview={handlePreview}
					onModify={handleModify}
				/>
			</Flex>
			<LogoPreviewModal
				isOpen={isPreviewOpen}
				onClose={handleClosePreview}
				logo={previewLogo}
				isSelected={previewLogo ? selectedLogoId === previewLogo.id : false}
				onSelect={() => previewLogo && handleSelect()}
				initialShowFeedback={openWithFeedback}
			/>
		</>
	);
};

RegeneratedLogoComponent.displayName = 'RegeneratedLogoComponent';

// Logo Options Grid - wraps generated logos in a horizontal layout (kept for backwards compatibility)
const LogoOptionsGridComponent: FC<{ children: ReactNode }> = ({ children }) => {
	return (
		<Flex gap={3} flexWrap="wrap" mt={2} justifyContent="flex-start">
			{children}
		</Flex>
	);
};

LogoOptionsGridComponent.displayName = 'LogoOptionsGridComponent';

const ChannelComponent: FC<{ channelType: ChannelType }> = ({
	channelType,
}) => {
	const { selectedChannel, setSelectedChannel } = useChatData();
	const { sendMessage } = useRealtimeChatContext();

	const handleChannelSelect = (channel: ChannelType) => {
		setSelectedChannel(channel);
		// Send message to chat indicating the selection
		const channelNames: Record<ChannelType, string> = {
			online: 'At home',
			instore: 'From my stores',
			both: 'Both (At home and from my stores)',
			digital: 'Digital Goods',
		};
		sendMessage(channelNames[channel]);
	};

	return (
		<ChannelSelector
			selectedChannel={selectedChannel || undefined}
			onChannelSelect={handleChannelSelect}
			type={channelType}
		/>
	);
};

/** Grid component that shows all channel options in 2x2 layout */
const ChannelOptionsComponent: FC = () => {
	const { selectedChannel, setSelectedChannel } = useChatData();
	const { sendMessage } = useRealtimeChatContext();

	const handleChannelSelect = (channel: ChannelType) => {
		setSelectedChannel(channel);
		// Send message to chat indicating the selection
		const channelNames: Record<ChannelType, string> = {
			online: 'At home',
			instore: 'From my stores',
			both: 'Both (At home and from my stores)',
			digital: 'Digital Goods',
		};
		sendMessage(channelNames[channel]);
	};

	return (
		<ChannelOptionsGrid
			selectedChannel={selectedChannel || undefined}
			onChannelSelect={handleChannelSelect}
		/>
	);
};

const Message: FC<IMessageProps> = ({
	content,
	onImageClick,
	onContentResize,
}) => {
	const navigate = useNavigate();
	const { sendMessage, closeCopilot } = useRealtimeChatContext();
	const contentRef = useRef<HTMLDivElement>(null);
	const { getCatalogById } = useCatalogsContext();
	// Extract bordered-container selection state at component level for reliable access
	const { clickedBorderedContent, setClickedBorderedContent } = useChatData();
	// Right sidebar context for opening support panel and closing sidebar
	const { handleOpenSidebar, handleCloseSidebar } = useRightSidebarContext();
	// Onboarding context for triggering product tour
	const { startTour } = useOnboarding();
	// Detect mobile device for hiding tour-related features
	const isMobileDevice = typeof window !== 'undefined' && window.screen.width < 768;

	// Check if contexts are ready - if not, don't render yet
	const contextsReady = Boolean(sendMessage && getCatalogById);

	// Force component to remount when contexts become available
	const [markdownKey, setMarkdownKey] = useState(0);

	useEffect(() => {
		if (contextsReady) {
			// Small delay to ensure contexts are fully initialized, then force remount
			const timer = setTimeout(() => {
				setMarkdownKey(prev => prev + 1);
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [contextsReady]);

	useEffect(() => {
		if (!contentRef.current || !onContentResize) return;

		// Debounce resize events to avoid rapid-fire scroll calls
		let resizeTimeout: NodeJS.Timeout | null = null;
		let lastHeight = 0;

		const resizeObserver = new ResizeObserver((entries) => {
			if (resizeTimeout) clearTimeout(resizeTimeout);

			// Only trigger resize if height actually changed significantly
			const entry = entries[0];
			const newHeight = entry.contentRect.height;
			const heightDiff = Math.abs(newHeight - lastHeight);

			// Ignore small height changes (< 5px) to reduce unnecessary scrolls
			if (heightDiff < 5) return;

			lastHeight = newHeight;
			resizeTimeout = setTimeout(() => {
				onContentResize();
			}, 100); // Increased debounce to 100ms for smoother scrolling with widgets
		});

		resizeObserver.observe(contentRef.current);
		return () => {
			if (resizeTimeout) clearTimeout(resizeTimeout);
			resizeObserver.disconnect();
		};
	}, [onContentResize]);

	const components = useMemo(
		() => ({
			a: ({ href, children }: { href: string; children: ReactNode }) => {
				if (!href) return <>{children}</>;
				const isInternal = href.startsWith('/');
				return (
					<ChakraLink
						color="blue.500"
						cursor="pointer"
						onClick={(e) => {
							e.preventDefault();
							if (isInternal) {
								handleCampaignNavigation(href);
								navigate(href);
							} else {
								window.open(href, '_blank', 'noopener');
							}
						}}
					>
						{children}
					</ChakraLink>
				);
			},
			img: ({ src, alt }: { src: string; alt?: string }) => (
				<LazyImage src={src} alt={alt} onImageClick={onImageClick} />
			),
			p: ({ children }: { children: ReactNode }) => {
				// Don't render empty paragraphs (caused by extra newlines in markdown)
				if (!children || (typeof children === 'string' && !children.trim())) {
					return null;
				}
				return (
					<Box as="div" fontSize={{ base: '16px', md: '14px' }} sx={{ '&:last-child': { mb: 0 }, mb: 2 }}>{children}</Box>
				);
			},
			ul: ({ children }: { children: ReactNode }) => (
				<List spacing={2} styleType="disc" pl={4}>
					{children}
				</List>
			),
			ol: ({ children }: { children: ReactNode }) => (
				<List as="ol" spacing={2} styleType="decimal" pl={4}>
					{children}
				</List>
			),
			li: ({ children }: { children: ReactNode }) => (
				<ListItem>{children}</ListItem>
			),
			'link-button': ({ node }: NodeProps) => {
				const { label, to } = node.properties as any;
				return (
					<Box display="flex" justifyContent="center">
						<Button
							variant="orangeSolid"
							onClick={() => {
								if (typeof to === 'string') {
									handleCampaignNavigation(to);
									navigate(to);
								}
							}}
						>
							{label}
						</Button>
					</Box>
				);
			},
			// Open support panel component - triggers the support sidebar
			'open-support-panel': () => {
				return (
					<Box display="flex" justifyContent="center" my={3}>
						<Button
							variant="orangeSolid"
							leftIcon={
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 13.8214 2.48697 15.5291 3.33782 17L2.5 21.5L7 20.6622C8.47087 21.513 10.1786 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
									<path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
									<circle cx="12" cy="16" r="1" fill="currentColor"/>
								</svg>
							}
							onClick={() => handleOpenSidebar('support')}
						>
							Contact Support
						</Button>
					</Box>
				);
			},
			'catalog-preview': ({ node }: NodeProps) => {
				const catalogId = node.properties?.catalogid as string;
				const { products } = useChatData();

				// Try to decode base64 catalogId (backend may send name encoded as base64)
				let decodedName = '';
				try {
					decodedName = atob(catalogId);
				} catch {
					// Not base64, use as-is
				}

				console.log('[catalog-preview] Rendering:', {
					catalogId,
					decodedName,
					productsCount: products?.length || 0,
					productIds: products?.map((p: any) => p.catalogId || p.id).slice(0, 5),
					productNames: products?.map((p: any) => p.name).slice(0, 5),
				});

				if (!catalogId) {
					console.error('❌ [Message] Missing required props for catalog-preview:', {
						catalogId,
						properties: node.properties
					});
					return null;
				}

				// First, try to find the specific catalog by ID in chat context
				// This handles freshly added products before global context syncs
				// Include base64 decoding to match product names
				const catalogFromContext = products?.find(
					(p: any) =>
						p.catalogId === catalogId ||
						p.id === catalogId ||
						p.name === catalogId ||
						(decodedName && p.name === decodedName)
				);

				console.log('[catalog-preview] Lookup result:', {
					catalogId,
					decodedName,
					foundInContext: !!catalogFromContext,
					foundName: catalogFromContext?.name,
				});

				if (catalogFromContext) {
					const actualCatalogId = catalogFromContext.catalogId || catalogFromContext.id;
					return (
						<Box
							w="100%"
							maxW="650px"
							mb={3}
							borderRadius="xl"
							boxShadow="0 0 4px rgba(0, 0, 0, 0.1)"
							_hover={{
								boxShadow: 'lg',
							}}
							transition="all 0.2s ease-in-out"
						>
							<CatalogPreview catalogId={actualCatalogId} />
						</Box>
					);
				}

				// Try index-based lookup from chat context (for freshly added products)
				const catalogIndex = parseInt(catalogId);
				if (!isNaN(catalogIndex) && products && products[catalogIndex]) {
					const catalog = products[catalogIndex];
					const actualCatalogId = catalog.catalogId || catalog.id;
					return (
						<Box
							w="100%"
							maxW="650px"
							mb={3}
							borderRadius="xl"
							boxShadow="0 0 4px rgba(0, 0, 0, 0.1)"
							_hover={{
								boxShadow: 'lg',
							}}
							transition="all 0.2s ease-in-out"
						>
							<CatalogPreview catalogId={actualCatalogId} />
						</Box>
					);
				}

				// Fallback: try ID-based lookup from global context
				return <CatalogPreviewWrapper catalogId={catalogId} />;
			},
			'catalog-select-preview': ({ node }: NodeProps) => {
				const catalogId = node.properties?.catalogid as string;
				const explicitSelected =
					node.properties?.selected === 'true' ||
					node.properties?.selected === true;
				const catalog = getCatalogById(catalogId);
				const { selectedCatalogId, setSelectedCatalogId, products } = useChatData();
				const [isLocalDeleted, setIsLocalDeleted] = useState(false);

				// Try to decode base64 catalogId (backend may send name encoded as base64)
				let decodedName = '';
				try {
					decodedName = atob(catalogId);
				} catch {
					// Not base64, use as-is
				}

				console.log('[catalog-select-preview] Rendering:', {
					catalogId,
					decodedName,
					explicitSelected,
					hasCatalog: !!catalog,
					productsCount: products?.length || 0,
					productNames: products?.map((p: any) => p.name).slice(0, 5),
				});

				if (!catalogId) {
					console.error('Missing required props for catalog-select-preview:', {
						catalogId,
					});
					return null;
				}

				// Listen for catalog deletion events
				React.useEffect(() => {
					const handleCatalogDeleted = (event: CustomEvent) => {
						if (event.detail?.catalogId === catalogId) {
							setIsLocalDeleted(true);
						}
					};

					window.addEventListener(
						'catalog-deleted',
						handleCatalogDeleted as EventListener,
					);

					return () => {
						window.removeEventListener(
							'catalog-deleted',
							handleCatalogDeleted as EventListener,
						);
					};
				}, [catalogId]);

				// Check if catalog exists in chat products (scraped catalogs)
				// Include base64 decoding to match product names
				const productFromChat = products?.find(
					(p: any) =>
						p.catalogId === catalogId ||
						p.id === catalogId ||
						p.name === catalogId ||
						(decodedName && p.name === decodedName)
				);

				console.log('[catalog-select-preview] Lookup result:', {
					catalogId,
					decodedName,
					foundInApi: !!catalog,
					foundInChat: !!productFromChat,
					productFromChatName: productFromChat?.name,
				});

				// Don't render if catalog doesn't exist in API or chat, or was deleted
				if ((!catalog && !productFromChat) || isLocalDeleted) {
					console.log('[catalog-select-preview] Not rendering - no catalog found or deleted');
					return null;
				}

				// Auto-select first catalog if none is selected and this catalog has explicit selection
				React.useEffect(() => {
					if (!selectedCatalogId && explicitSelected) {
						setSelectedCatalogId(catalogId);
					}
				}, [
					catalogId,
					selectedCatalogId,
					setSelectedCatalogId,
					explicitSelected,
				]);

				// Use chat context to determine selection state dynamically
				const isSelected = selectedCatalogId === catalogId;

				const handleClick = () => {
					// Update selection in chat context
					setSelectedCatalogId(catalogId);

					const catalogName = catalog?.name || productFromChat?.name;
					if (catalogName) {
						sendMessage(`Let's use ${catalogName}`);
					}
				};

				return (
					<Box w="100%" maxW="500px">
						<CatalogSelectPreview
							catalogId={catalogId}
							selected={isSelected}
							onClick={handleClick}
						/>
					</Box>
				);
			},
			'location-preview': ({ node }: NodeProps) => {
				const { locationid } = node.properties as any;

				if (locationid === undefined) {
					console.error('location-preview: Missing locationid property');
					return null;
				}

				// Render the specific location by its index
				return <LocationPreviewComponent locationId={parseInt(locationid)} />;
			},
			'customer-profile-card': ({ node }: NodeProps) => {
				const { profileid } = node.properties as any;

				if (profileid === undefined) {
					console.error('customer-profile-card: Missing profileid property');
					return null;
				}

				// Render the specific profile by its index
				return <CustomerProfileCardComponent profileIndex={parseInt(profileid)} />;
			},
			'service-area-preview': ({ node }: NodeProps) => {
				const { serviceareaid } = node.properties as any;

				if (serviceareaid === undefined) {
					console.error('service-area-preview: Missing serviceareaid property');
					return null;
				}

				// Render the specific service area by its index
				return (
					<ServiceAreaPreviewComponent
						serviceAreaId={parseInt(serviceareaid)}
					/>
				);
			},

			'bordered-container': ({ node, children }: NodeProps) => {
				const { clickable, action, selected } = node.properties as any;
				
				// Default to clickable for confirmation flow
				const isClickable = clickable !== 'false';
				
				// Extract text content for comparison - handle multiple nested structures
				const extractTextContent = (child: ReactNode): string => {
					if (typeof child === 'string') return child.trim();
					if (typeof child === 'number') return String(child);
					if (Array.isArray(child)) return child.map(extractTextContent).join('').trim();
					if (isValidElement(child)) {
						const props = child.props as any;
						if (props?.children) return extractTextContent(props.children);
					}
					return '';
				};
				const textContent = extractTextContent(children);
				
				// Check if this option is selected:
				// 1. User clicked on it (matches clickedBorderedContent from parent scope)
				// 2. Backend says it's selected ONLY for "select" action (industry/product selection)
				//    NOT for confirmation actions like "Save this profile" where user decides
				const isBackendSelected = selected === 'true' || selected === true;
				const isUserClicked = clickedBorderedContent !== null && textContent === clickedBorderedContent;
				// Only auto-select for "select" action, never for confirmation actions
				const isSelected = isUserClicked || (action === 'select' && isBackendSelected && clickedBorderedContent === null);
				
				// Debug log for selection state
				if (action === 'select') {
					console.log('📦 [bordered-container] Selection check:', {
						textContent,
						clickedBorderedContent,
						isBackendSelected,
						isUserClicked,
						isSelected,
					});
				}

				const handleClick = () => {
					if (isClickable) {
						// action="select" sends the content (for industry/product selection)
						// action="confirm" or default sends "yes" (for field confirmation)
						if (action === 'select') {
							if (textContent) {
								// Update selection state (from parent scope)
								setClickedBorderedContent(textContent);
								sendMessage(textContent);
							}
						} else {
							sendMessage('yes');
						}
					}
				};

				return (
					<Box
						border="1px solid"
						borderColor={isSelected ? 'blue.500' : 'gray.300'}
						borderRadius="md"
						bg={isSelected ? 'blue.50' : 'gray.50'}
						p={4}
						fontSize="14px"
						cursor={isClickable ? 'pointer' : 'default'}
						onClick={handleClick}
						_hover={
							isClickable
								? {
										bg: isSelected ? 'blue.100' : 'gray.100',
										borderColor: isSelected ? 'blue.600' : 'gray.400',
									}
								: {}
						}
						transition="all 0.2s ease-in-out"
					>
						{children}
					</Box>
				);
			},
			// Container for grouping suggestion options horizontally
			'suggestion-options': ({ children }: NodeProps) => {
				return (
					<Flex
						flexWrap="wrap"
						gap={2}
						mt={3}
						mb={2}
					>
						{children}
					</Flex>
				);
			},
			'suggestion-option': ({ node, children }: NodeProps) => {
				const { value } = node.properties as any;
				const displayValue = value || (typeof children === 'string' ? children.trim() : '');

				const handleClick = () => {
					if (displayValue) {
						sendMessage(displayValue);
					}
				};

				return (
					<Box
						as="button"
						display="inline-flex"
						alignItems="center"
						justifyContent="center"
						gap={1.5}
						border="1px solid"
						borderColor="gray.200"
						borderRadius="full"
						bg="white"
						color="gray.700"
						px={4}
						py={2}
						h="36px"
						minH="36px"
						fontSize="13px"
						fontWeight="500"
						fontFamily="'Noto Sans', 'Open Sans', sans-serif"
						cursor="pointer"
						onClick={handleClick}
						boxShadow="0 1px 2px rgba(0,0,0,0.05)"
						lineHeight="1"
						_hover={{
							bg: 'orange.50',
							borderColor: 'orange.300',
							color: 'orange.600',
							boxShadow: '0 2px 8px rgba(240, 77, 36, 0.15)',
							transform: 'translateY(-1px)',
						}}
						_active={{
							bg: 'orange.100',
							borderColor: 'orange.400',
							color: 'orange.700',
							transform: 'translateY(0)',
							boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
						}}
						transition="all 0.2s ease"
					>
						{children || displayValue}
					</Box>
				);
			},
			// Service selection container - wraps service-selection-card components
			// Uses grid layout (2x2 on desktop, 1 column on mobile) when layout="grid", otherwise vertical
			'service-selection-container': ({ node, children }: NodeProps) => {
				const { layout } = node.properties as any;
				const isGrid = layout === 'grid';

				// DEBUG: Log children to understand what's being passed
				console.log('🎴 [service-selection-container] Rendering:', {
					childrenType: typeof children,
					isArray: Array.isArray(children),
					childCount: Array.isArray(children) ? children.length : (children ? 1 : 0),
					children: children,
					nodeChildren: node.children?.length,
					nodeChildrenTags: node.children?.map((c: any) => c.tagName || c.type),
				});

				if (isGrid) {
					return (
						<Box
							display="grid"
							gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
							gap={3}
							w="100%"
							maxW={{ base: '100%', md: '660px' }}
							sx={{
								'& > *': {
									height: '100%',
								}
							}}
						>
							{children}
						</Box>
					);
				}

				return (
					<Flex
						direction="column"
						gap={3}
						w="100%"
						maxW="320px"
					>
						{children}
					</Flex>
				);
			},
			// Service selection card - used for initial service choice (Campaign vs AI Voice Agent)
			'service-selection-card': ({ node }: NodeProps) => {
				const { value, icon, title, description } = node.properties as any;

				// Hide "Take a Tour" on mobile - tour is not available on mobile devices
				if (value === 'Take a Tour' && isMobileDevice) {
					return null;
				}

				const handleClick = () => {
					if (value === 'Take a Tour') {
						// Close the copilot chat first, then show the Welcome Modal
						closeCopilot();
						handleCloseSidebar();
						setTimeout(() => {
							startTour(window.location.pathname);
						}, 150);
						return;
					}
					if (value) {
						sendMessage(value);
					}
				};

				// Icon SVGs based on the icon prop
				const renderIcon = () => {
					if (icon === 'campaign') {
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M21 10V8C21 6.89543 20.1046 6 19 6H5C3.89543 6 3 6.89543 3 8V16C3 17.1046 3.89543 18 5 18H12" stroke="#F04D24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
								<path d="M7 15L10 12L13 15L17 11" stroke="#F04D24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
								<path d="M18 14V20M15 17H21" stroke="#F04D24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
								<circle cx="17" cy="11" r="1" fill="#F04D24"/>
							</svg>
						);
					}
					if (icon === 'voice') {
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z" stroke="#F04D24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
								<path d="M19 10V12C19 15.866 15.866 19 12 19C8.134 19 5 15.866 5 12V10" stroke="#F04D24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
								<path d="M12 19V23M8 23H16" stroke="#F04D24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
								<circle cx="19" cy="5" r="3" fill="#F04D24" opacity="0.3"/>
								<path d="M19 4V6M18 5H20" stroke="#F04D24" strokeWidth="1" strokeLinecap="round"/>
							</svg>
						);
					}
					if (icon === 'logo' || icon === 'create_logo') {
						// ICON OPTIONS - Change iconOption to 1, 2, 3, 4, or 5 to preview different styles
						const iconOption = 1;

						if (iconOption === 1) {
							// Option 1: Flat palette with brush - simple and clean
							return (
								<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									{/* Flat palette */}
									<path d="M12 3C7 3 3 7 3 12C3 17 7 21 12 21C13 21 13.5 20.5 13.5 19.5C13.5 19 13.3 18.6 13 18.3C12.7 18 12.5 17.6 12.5 17C12.5 16 13.3 15 14.5 15H16C19 15 21 13 21 10C21 6 17 3 12 3Z" fill="#F9DDD6"/>
									{/* Paint dots */}
									<circle cx="7" cy="10" r="1.5" fill="#22D3EE"/>
									<circle cx="10" cy="7" r="1.5" fill="#FB923C"/>
									<circle cx="7" cy="14" r="1.5" fill="#A855F7"/>
									<circle cx="10.5" cy="12" r="1.5" fill="#F04D24"/>
									{/* Brush */}
									<rect x="16" y="2" width="3" height="6" rx="1" fill="#F04D24" transform="rotate(15 17.5 5)"/>
									<rect x="17" y="7" width="2" height="4" fill="#5EEAD4" transform="rotate(15 18 9)"/>
								</svg>
							);
						}
						if (iconOption === 2) {
							// Option 2: Minimal flat palette - no brush, just palette with dots
							return (
								<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									{/* Simple flat palette shape */}
									<path d="M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C13.1 22 14 21.1 14 20C14 19.5 13.8 19 13.5 18.7C13.2 18.4 13 17.9 13 17.4C13 16.3 13.9 15.4 15 15.4H17C19.8 15.4 22 13.2 22 10.4C22 5.7 17.5 2 12 2Z" fill="#F9DDD6"/>
									{/* Thumb hole */}
									<circle cx="15" cy="10" r="2" fill="white"/>
									{/* Colorful paint spots */}
									<circle cx="7" cy="9" r="2" fill="#22D3EE"/>
									<circle cx="10" cy="6" r="2" fill="#FB923C"/>
									<circle cx="7" cy="14" r="2" fill="#A855F7"/>
									<circle cx="11" cy="12" r="2" fill="#F04D24"/>
									<circle cx="10" cy="17" r="1.5" fill="#FACC15"/>
								</svg>
							);
						}
						if (iconOption === 3) {
							// Option 3: Outlined palette with brush beside it
							return (
								<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									{/* Palette outline */}
									<path d="M10 3C5.5 3 2 6.5 2 11C2 15.5 5.5 19 10 19C10.8 19 11.5 18.3 11.5 17.5C11.5 17.1 11.3 16.7 11.1 16.4C10.8 16.1 10.6 15.7 10.6 15.2C10.6 14.3 11.3 13.5 12.3 13.5H14C16.5 13.5 18.5 11.5 18.5 9C18.5 5.5 14.8 3 10 3Z" stroke="#F04D24" strokeWidth="1.5" fill="none"/>
									{/* Paint dots */}
									<circle cx="6" cy="9" r="1.3" fill="#22D3EE"/>
									<circle cx="8.5" cy="6" r="1.3" fill="#FB923C"/>
									<circle cx="6" cy="13" r="1.3" fill="#A855F7"/>
									<circle cx="9" cy="11" r="1.3" fill="#EF4444"/>
									{/* Brush on side */}
									<rect x="20" y="5" width="2" height="8" rx="1" fill="#5EEAD4"/>
									<path d="M20 5L21 2L22 5H20Z" fill="#2A2A2A"/>
									<rect x="20" y="13" width="2" height="3" fill="#C4B5A0"/>
								</svg>
							);
						}
						if (iconOption === 4) {
							// Option 4: Modern flat with diagonal brush
							return (
								<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									{/* Flat palette */}
									<path d="M11 4C6.5 4 3 7.5 3 12C3 16.5 6.5 20 11 20C11.8 20 12.5 19.3 12.5 18.5C12.5 18 12.3 17.6 12 17.3C11.7 17 11.5 16.5 11.5 16C11.5 15 12.3 14 13.5 14H15.5C18.3 14 20.5 11.8 20.5 9C20.5 6 17 4 11 4Z" fill="#F9DDD6"/>
									{/* Paint dots */}
									<circle cx="6.5" cy="10" r="1.5" fill="#22D3EE"/>
									<circle cx="9" cy="7" r="1.5" fill="#FB923C"/>
									<circle cx="6.5" cy="14" r="1.5" fill="#A855F7"/>
									<circle cx="10" cy="11.5" r="1.5" fill="#F04D24"/>
									{/* Diagonal brush */}
									<path d="M15 6L21 12" stroke="#5EEAD4" strokeWidth="3" strokeLinecap="round"/>
									<path d="M20 11L22 13L21 14L19 12" fill="#2A2A2A"/>
								</svg>
							);
						}
						if (iconOption === 5) {
							// Option 5: Super minimal - just palette shape with dots
							return (
								<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									{/* Ultra simple palette */}
									<ellipse cx="11" cy="12" rx="9" ry="8" fill="#F9DDD6"/>
									{/* Thumb hole */}
									<circle cx="15" cy="12" r="2.5" fill="white"/>
									{/* Paint dots in arc */}
									<circle cx="6" cy="9" r="2" fill="#22D3EE"/>
									<circle cx="9" cy="6" r="2" fill="#FB923C"/>
									<circle cx="6" cy="15" r="2" fill="#A855F7"/>
									<circle cx="10" cy="13" r="2" fill="#F04D24"/>
									<circle cx="13" cy="7" r="1.5" fill="#FACC15"/>
								</svg>
							);
						}
						// Default fallback
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<ellipse cx="11" cy="12" rx="9" ry="8" fill="#F9DDD6"/>
								<circle cx="15" cy="12" r="2.5" fill="white"/>
								<circle cx="6" cy="9" r="2" fill="#22D3EE"/>
								<circle cx="9" cy="6" r="2" fill="#FB923C"/>
								<circle cx="6" cy="15" r="2" fill="#A855F7"/>
								<circle cx="10" cy="13" r="2" fill="#F04D24"/>
							</svg>
						);
					}
					if (icon === 'create_website') {
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								{/* Globe with code/browser elements - modern website icon */}
								<circle cx="12" cy="12" r="9" stroke="#F04D24" strokeWidth="1.5"/>
								<ellipse cx="12" cy="12" rx="4" ry="9" stroke="#F04D24" strokeWidth="1.5"/>
								<path d="M3 12H21" stroke="#F04D24" strokeWidth="1.5"/>
								<path d="M5 7H19" stroke="#F04D24" strokeWidth="1.5"/>
								<path d="M5 17H19" stroke="#F04D24" strokeWidth="1.5"/>
								{/* Sparkle accent for AI/creation */}
								<path d="M19 5L20 4M20 4L21 3M20 4L21 5M20 4L19 3" stroke="#F04D24" strokeWidth="1.2" strokeLinecap="round"/>
							</svg>
						);
					}
					if (icon === 'tour' || icon === 'onboarding') {
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								{/* Screen/window with play button - represents guided tour/onboarding */}
								<rect x="3" y="4" width="18" height="14" rx="2" stroke="#F04D24" strokeWidth="1.5"/>
								{/* Top bar of window */}
								<path d="M3 8H21" stroke="#F04D24" strokeWidth="1.5"/>
								{/* Window dots */}
								<circle cx="5.5" cy="6" r="0.75" fill="#F04D24"/>
								<circle cx="8" cy="6" r="0.75" fill="#F04D24"/>
								{/* Play button in center */}
								<path d="M10 11L15 13.5L10 16V11Z" fill="#F04D24"/>
								{/* Cursor/pointer at bottom */}
								<path d="M17 18L19 20L21 19L19.5 17L21 15.5L17 14V18Z" fill="#F04D24"/>
							</svg>
						);
					}
					// Sparkles icon - for AI generation
					if (icon === 'sparkles') {
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M12 3L13.5 8.5L19 10L13.5 11.5L12 17L10.5 11.5L5 10L10.5 8.5L12 3Z" fill="#F04D24"/>
								<path d="M19 15L19.75 17.25L22 18L19.75 18.75L19 21L18.25 18.75L16 18L18.25 17.25L19 15Z" fill="#F04D24" opacity="0.7"/>
								<path d="M5 2L5.5 3.5L7 4L5.5 4.5L5 6L4.5 4.5L3 4L4.5 3.5L5 2Z" fill="#F04D24" opacity="0.5"/>
							</svg>
						);
					}
					// Box icon - for products
					if (icon === 'box') {
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M21 8L12 2L3 8V16L12 22L21 16V8Z" stroke="#F04D24" strokeWidth="1.5" strokeLinejoin="round"/>
								<path d="M12 22V12" stroke="#F04D24" strokeWidth="1.5"/>
								<path d="M21 8L12 12L3 8" stroke="#F04D24" strokeWidth="1.5"/>
								<path d="M7.5 5L16.5 10" stroke="#F04D24" strokeWidth="1.5"/>
							</svg>
						);
					}
					// Users icon - for customer profiles
					if (icon === 'users') {
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="9" cy="7" r="4" stroke="#F04D24" strokeWidth="1.5"/>
								<path d="M3 21V19C3 16.79 4.79 15 7 15H11C13.21 15 15 16.79 15 19V21" stroke="#F04D24" strokeWidth="1.5" strokeLinecap="round"/>
								<circle cx="17" cy="8" r="3" stroke="#F04D24" strokeWidth="1.5"/>
								<path d="M17 14C19.21 14 21 15.79 21 18V21" stroke="#F04D24" strokeWidth="1.5" strokeLinecap="round"/>
							</svg>
						);
					}
					// Map-pin icon - for locations
					if (icon === 'map-pin') {
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M12 21C12 21 19 14.5 19 9C19 5.134 15.866 2 12 2C8.134 2 5 5.134 5 9C5 14.5 12 21 12 21Z" stroke="#F04D24" strokeWidth="1.5"/>
								<circle cx="12" cy="9" r="3" stroke="#F04D24" strokeWidth="1.5"/>
							</svg>
						);
					}
					// Plus icon - for adding new items
					if (icon === 'plus') {
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="12" cy="12" r="9" stroke="#F04D24" strokeWidth="1.5"/>
								<path d="M12 8V16M8 12H16" stroke="#F04D24" strokeWidth="1.5" strokeLinecap="round"/>
							</svg>
						);
					}
					// Map icon - for service areas
					if (icon === 'map') {
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M3 6L9 3L15 6L21 3V18L15 21L9 18L3 21V6Z" stroke="#F04D24" strokeWidth="1.5" strokeLinejoin="round"/>
								<path d="M9 3V18" stroke="#F04D24" strokeWidth="1.5"/>
								<path d="M15 6V21" stroke="#F04D24" strokeWidth="1.5"/>
							</svg>
						);
					}
					// Video Ad icon
					if (icon === 'video_ad') {
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								{/* Film/clapperboard with play button */}
								<rect x="3" y="6" width="18" height="13" rx="2" stroke="#F04D24" strokeWidth="1.5"/>
								{/* Clapperboard top */}
								<path d="M3 6L6 3H18L21 6" stroke="#F04D24" strokeWidth="1.5" strokeLinejoin="round"/>
								<path d="M9 3L7 6" stroke="#F04D24" strokeWidth="1.2"/>
								<path d="M13 3L11 6" stroke="#F04D24" strokeWidth="1.2"/>
								<path d="M17 3L15 6" stroke="#F04D24" strokeWidth="1.2"/>
								{/* Play button */}
								<path d="M10 10L16 12.5L10 15V10Z" fill="#F04D24"/>
							</svg>
						);
					}
					// List icon - for existing items
					if (icon === 'list') {
						return (
							<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M8 6H21" stroke="#F04D24" strokeWidth="1.5" strokeLinecap="round"/>
								<path d="M8 12H21" stroke="#F04D24" strokeWidth="1.5" strokeLinecap="round"/>
								<path d="M8 18H21" stroke="#F04D24" strokeWidth="1.5" strokeLinecap="round"/>
								<circle cx="4" cy="6" r="1.5" fill="#F04D24"/>
								<circle cx="4" cy="12" r="1.5" fill="#F04D24"/>
								<circle cx="4" cy="18" r="1.5" fill="#F04D24"/>
							</svg>
						);
					}
					return null;
				};

				return (
					<Box
						as="button"
						display="flex"
						flexDirection="column"
						alignItems="flex-start"
						justifyContent="flex-start"
						w="100%"
						h="195px"
						border="2px solid"
						borderColor="gray.200"
						borderRadius="xl"
						bg="white"
						p={4}
						cursor="pointer"
						onClick={handleClick}
						_hover={{
							borderColor: 'orange.base',
							bg: 'orange.50',
							transform: 'translateY(-2px)',
							boxShadow: 'lg',
						}}
						_active={{
							borderColor: 'orange.primary',
							bg: 'orange.100',
							transform: 'translateY(0)',
						}}
						transition="all 0.2s ease-in-out"
						textAlign="left"
					>
						<Flex align="center" gap={3} mb={2} flexShrink={0}>
							<Box
								bg="orange.50"
								borderRadius="lg"
								p={2}
								display="flex"
								alignItems="center"
								justifyContent="center"
							>
								{renderIcon()}
							</Box>
							<Text
								fontSize="16px"
								fontWeight="semibold"
								color="gray.800"
								fontFamily="'Noto Sans', 'Open Sans', sans-serif"
							>
								{title}
							</Text>
						</Flex>
						<Text
							fontSize="13px"
							color="gray.600"
							fontFamily="'Noto Sans', 'Open Sans', sans-serif"
							lineHeight="1.5"
						>
							{description}
						</Text>
					</Box>
				);
			},
			// Industry options container - wraps industry-option components
			'industry-options': ({ children }: NodeProps) => {
				return (
					<Flex
						direction="column"
						gap={2}
						w="100%"
						maxW="400px"
					>
						{children}
					</Flex>
				);
			},
			// Industry option - clickable industry selection button
			'industry-option': ({ node, children }: NodeProps) => {
				const { value, recommended } = node.properties as any;
				const displayValue = value || (typeof children === 'string' ? children.trim() : '');
				const isRecommended = recommended === 'true' || recommended === true;

				const handleClick = () => {
					if (displayValue) {
						sendMessage(displayValue);
					}
				};

				return (
					<Box
						as="button"
						w="100%"
						p={3}
						border="2px solid"
						borderColor={isRecommended ? 'orange.base' : 'gray.200'}
						borderRadius="lg"
						bg={isRecommended ? 'orange.50' : 'white'}
						cursor="pointer"
						onClick={handleClick}
						textAlign="left"
						display="flex"
						alignItems="center"
						gap={3}
						_hover={{
							borderColor: 'orange.base',
							bg: 'orange.50',
							transform: 'translateX(4px)',
						}}
						_active={{
							borderColor: 'orange.primary',
							bg: 'orange.100',
						}}
						transition="all 0.15s ease-in-out"
					>
						<Box
							w="20px"
							h="20px"
							borderRadius="full"
							border="2px solid"
							borderColor={isRecommended ? 'orange.base' : 'gray.300'}
							bg={isRecommended ? 'orange.base' : 'transparent'}
							display="flex"
							alignItems="center"
							justifyContent="center"
							flexShrink={0}
						>
							{isRecommended && (
								<Box w="8px" h="8px" borderRadius="full" bg="white" />
							)}
						</Box>
						<Text
							fontSize="14px"
							fontWeight={isRecommended ? 'semibold' : 'medium'}
							color="gray.800"
							fontFamily="'Noto Sans', 'Open Sans', sans-serif"
						>
							{displayValue}
							{isRecommended && (
								<Text as="span" fontSize="12px" color="orange.base" ml={2}>
									(Recommended)
								</Text>
							)}
						</Text>
					</Box>
				);
			},
			// Product suggestion - selectable card similar to catalog-select-preview
			// Used when showing product/service suggestions with radio-style selection
			'product-suggestion': ({ node, children }: NodeProps) => {
				const { index: suggestionIndex, value, selected } = node.properties as any;
				const displayValue = value || (typeof children === 'string' ? children.trim() : '');
				const indexNum = parseInt(suggestionIndex || '0', 10);
				const isInitiallySelected = selected === 'true' || selected === true;
				
				// Use shared state for selection (first one selected by default)
				const { selectedSuggestionIndex, setSelectedSuggestionIndex } = useChatData();
				
				// Auto-select first suggestion if none is selected
				React.useEffect(() => {
					if (selectedSuggestionIndex === null && isInitiallySelected) {
						setSelectedSuggestionIndex(indexNum);
					}
				}, [indexNum, selectedSuggestionIndex, setSelectedSuggestionIndex, isInitiallySelected]);
				
				const isSelected = selectedSuggestionIndex === indexNum;

				const handleClick = () => {
					// Update selection state
					setSelectedSuggestionIndex(indexNum);
					// Send message with the selected value
					if (displayValue) {
						sendMessage(displayValue);
					}
				};

				return (
					<Box
						w="100%"
						maxW="500px"
						mb={2}
						border="2px solid"
						borderColor={isSelected ? 'orange.base' : 'gray.200'}
						borderRadius="xl"
						bg={isSelected ? 'orange.50' : 'white'}
						p={4}
						cursor="pointer"
						onClick={handleClick}
						_hover={{
							borderColor: isSelected ? 'orange.primary' : 'gray.300',
							bg: isSelected ? 'orange.100' : 'gray.50',
							boxShadow: 'md',
						}}
						transition="all 0.2s ease-in-out"
						position="relative"
					>
						<Flex align="center" gap={3}>
							{/* Radio-style indicator */}
							<Box
								w="20px"
								h="20px"
								borderRadius="full"
								border="2px solid"
								borderColor={isSelected ? 'orange.base' : 'gray.300'}
								bg={isSelected ? 'orange.base' : 'white'}
								display="flex"
								alignItems="center"
								justifyContent="center"
								flexShrink={0}
							>
								{isSelected && (
									<Box
										w="8px"
										h="8px"
										borderRadius="full"
										bg="white"
									/>
								)}
							</Box>
							{/* Content */}
							<Text
								fontSize="14px"
								fontWeight={isSelected ? '600' : 'normal'}
								color={isSelected ? 'gray.800' : 'gray.600'}
								fontFamily="'Noto Sans', 'Open Sans', sans-serif"
							>
								{children || displayValue}
							</Text>
						</Flex>
					</Box>
				);
			},
			'small-logo': ({ node }: NodeProps) => {
				const { url } = node.properties as any;
				console.log('🖼️ [small-logo] Rendering with properties:', node.properties);
				
				if (!url) {
					console.warn('🖼️ [small-logo] No URL provided, not rendering');
					return null;
				}

				return (
					<Box
						position="relative"
						display="inline-block"
						mb={3}
						borderRadius="lg"
						overflow="hidden"
						border="1px solid"
						borderColor="orange.300"
						bg="orange.50"
						p={3}
					>
						<Image
							src={url}
							alt="Logo (too small)"
							maxH="120px"
							maxW="200px"
							objectFit="contain"
							borderRadius="md"
							opacity={0.8}
						/>
						<Badge
							position="absolute"
							top={1}
							right={1}
							bg="orange.500"
							color="white"
							fontSize="10px"
							px={2}
							py={0.5}
							borderRadius="md"
						>
							⚠️
						</Badge>
					</Box>
				);
			},
			'channel-option': ({ node }: NodeProps) => {
				const { type } = node.properties as any;
				console.log('📺 [channel-option component] Rendering channel:', { type, nodeProperties: node.properties });
				return <ChannelComponent channelType={type} />;
			},
			'channel-options': () => {
				console.log('📺 [channel-options component] Rendering all channels in grid');
				return <ChannelOptionsComponent />;
			},
			'logo-options': () => {
				return <LogoOptionsComponent />;
			},
			'logo-options-grid': ({ children }: { children: ReactNode }) => {
				return <LogoOptionsGridComponent>{children}</LogoOptionsGridComponent>;
			},
			'account-logo-preview': ({ node }: NodeProps) => {
				const { url } = node.properties as any;
				return <AccountLogoPreviewComponent url={url} />;
			},
			'logo-options-with-existing': () => {
				return <AccountLogoOptionsComponent />;
			},
			'generated-logo': ({ node }: NodeProps) => {
				const { id, url, style } = node.properties as any;
				return <GeneratedLogoComponent id={id} url={url} style={style} />;
			},
			'regenerated-logo': ({ node }: NodeProps) => {
				const { id, url, style } = node.properties as any;
				return <RegeneratedLogoComponent id={id} url={url} style={style} />;
			},
			'retry-action': () => {
				return <RetryActionComponent />;
			},
			// Website creation components
			'website-offer': () => {
				return <WebsiteOffer />;
			},
			'domain-selector': ({ node }: NodeProps) => {
				const { domains } = node.properties as any;
				return <DomainSelector domains={domains} />;
			},
			'domain-confirmation': ({ node }: NodeProps) => {
				const { domain } = node.properties as any;
				return <DomainConfirmation domain={domain} />;
			},
			'website-preview': ({ node }: NodeProps) => {
				const { status, domain, url } = node.properties as any;
				return <WebsitePreview status={status} domain={domain} url={url} />;
			},
			'logo-completion': ({ node }: NodeProps) => {
				const { url } = node.properties as any;
				return <LogoCompletion url={url} />;
			},
			'voice-agent-completion': ({ node }: NodeProps) => {
				const { businessname, industry, phone } = node.properties as any;
				return <VoiceAgentCompletion businessName={businessname} industry={industry} phone={phone} />;
			},
			// Campaign success component - horizontal banner style with green success color
			'campaign-success': ({ node }: NodeProps) => {
				const { title, campaignurl } = node.properties as any;

				const handleViewCampaign = () => {
					if (campaignurl) {
						handleCampaignNavigation(campaignurl);
						navigate(campaignurl);
					}
				};

				return (
					<Flex
						w="100%"
						maxW="420px"
						bg="green.50"
						borderRadius="lg"
						border="1px solid"
						borderColor="green.200"
						p={3}
						my={2}
						direction={{ base: 'column', sm: 'row' }}
						align={{ base: 'flex-start', sm: 'center' }}
						gap={3}
					>
						<Flex align="center" gap={3} minW={0} flex={1}>
							{/* Success Icon */}
							<Box
								w="36px"
								h="36px"
								bg="green.500"
								borderRadius="full"
								display="flex"
								alignItems="center"
								justifyContent="center"
								flexShrink={0}
							>
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M20 6L9 17L4 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
								</svg>
							</Box>

							{/* Text Content */}
							<Box flex={1} minW={0}>
								<Text fontSize="sm" fontWeight="semibold" color="green.800" lineHeight="short">
									Your campaign is ready to go live!
								</Text>
								<Text fontSize="xs" color="green.700" noOfLines={1} title={title}>
									{title || 'Your Campaign'}
								</Text>
							</Box>
						</Flex>

						{/* Review Button */}
						<Button
							size="sm"
							colorScheme="green"
							onClick={handleViewCampaign}
							flexShrink={0}
							w={{ base: '100%', sm: 'auto' }}
							rightIcon={
								<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
								</svg>
							}
						>
							Review Campaign
						</Button>
					</Flex>
				);
			},
			// Design Direction components
			'dd-preview': ({ node }: NodeProps) => {
				const { id, variant, status, thumbnail, selected } = node.properties as any;
				const { selectedDesignDirectionId, setSelectedDesignDirectionId } = useChatData();

				const isSelected = selected === 'true' || selectedDesignDirectionId === id;

				const designDirection = {
					id: id || '',
					variant: variant || 'A',
					status: status || 'GENERATED',
					attributes: thumbnail ? { elements: [{ source: thumbnail }] } : undefined,
				};

				return (
					<Box maxW="280px" my={2}>
						<DDPreviewCard
							designDirection={designDirection}
							isSelected={isSelected}
							onSelect={() => {
								setSelectedDesignDirectionId(id);
								sendMessage(`I want to use design direction ${variant}`);
							}}
							onRegenerate={(feedback) => {
								const feedbackText = feedback ? ` with feedback: ${feedback}` : '';
								sendMessage(`Regenerate design direction ${variant}${feedbackText}`);
							}}
							onRemove={() => {
								sendMessage(`Remove design direction ${variant}`);
							}}
						/>
					</Box>
				);
			},
			'dd-options': ({ node }: NodeProps) => {
				const { campaignid } = node.properties as any;
				const { selectedDesignDirectionId, setSelectedDesignDirectionId } = useChatData();

				if (!campaignid) {
					console.error('[dd-options] Missing campaignid property');
					return null;
				}

				return (
					<DDOptionsGrid
						campaignId={campaignid}
						selectedDesignDirectionId={selectedDesignDirectionId}
						onSelect={(id) => setSelectedDesignDirectionId(id)}
					/>
				);
			},
			'dd-add-button': ({ node }: NodeProps) => {
				const { campaignid } = node.properties as any;

				const handleAddNew = () => {
					sendMessage('Create another design direction');
				};

				return (
					<Box my={3}>
						<Button
							leftIcon={
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
								</svg>
							}
							variant="outline"
							colorScheme="orange"
							size="sm"
							onClick={handleAddNew}
						>
							Add New Design
						</Button>
					</Box>
				);
			},
			// Video Ad components
			'video-ad-generating': ({ node }: NodeProps) => {
				const { videoadid } = node.properties as any;
				if (!videoadid) return null;
				return <VideoAdGenerating videoAdId={videoadid} />;
			},
			'video-ad-catalog-list': ({ children }: { children: ReactNode }) => {
				return (
					<VideoAdCatalogList onSelect={() => {}}>
						{children}
					</VideoAdCatalogList>
				);
			},
			'video-ad-catalog-item': ({ node }: NodeProps) => {
				const { catalogid, name, imageurl, type } = node.properties as any;
				if (!catalogid) return null;
				return (
					<VideoAdCatalogItem
						catalogId={catalogid}
						name={name || 'Untitled'}
						imageUrl={imageurl}
						type={type}
						onSelect={() => {
							// Special case: "Create new" card sends a natural-language phrase
							// that the backend recognizes as the "open product modal" intent.
							// For regular catalog picks, we send the catalog name so the user
							// bubble shows something human-readable (the backend matches by name).
							if (catalogid === '__create_new__') {
								sendMessage("I'd like to create a new product or service");
							} else {
								sendMessage(name || 'Untitled');
							}
						}}
					/>
				);
			},
			'video-ad-create-catalog': () => {
				// This tag is no longer used — modal is opened via components[] from backend
				// Kept as fallback in case it appears in older messages
				return null;
			},
			div: ({ children }: { children: ReactNode }) => {
				return <div>{children}</div>;
			},
		}),
		[navigate, onImageClick, sendMessage, getCatalogById, contextsReady],
	);

	const processedContent = preprocessContent(content.trim());

	// Check if we have custom tags that need context
	const hasCustomTags =
		processedContent.includes('<channel-option') ||
		processedContent.includes('<channel-options') ||
		processedContent.includes('<catalog-preview') ||
		processedContent.includes('<link-button') ||
		processedContent.includes('<open-support-panel') ||
		processedContent.includes('<customer-profile-card') ||
		processedContent.includes('<location-preview') ||
		processedContent.includes('<service-area-preview') ||
		processedContent.includes('<logo-options') ||
		processedContent.includes('<logo-options-grid') ||
		processedContent.includes('<account-logo-preview') ||
		processedContent.includes('<logo-options-with-existing') ||
		processedContent.includes('<generated-logo') ||
		processedContent.includes('<regenerated-logo') ||
		processedContent.includes('<retry-action') ||
		processedContent.includes('<website-offer') ||
		processedContent.includes('<domain-selector') ||
		processedContent.includes('<domain-confirmation') ||
		processedContent.includes('<website-preview') ||
		processedContent.includes('<logo-completion') ||
		processedContent.includes('<voice-agent-completion') ||
		processedContent.includes('<campaign-success') ||
		processedContent.includes('<suggestion-option') ||
		processedContent.includes('<product-suggestion') ||
		processedContent.includes('<small-logo') ||
		processedContent.includes('<service-selection-card') ||
		processedContent.includes('<service-selection-container') ||
		processedContent.includes('<industry-options') ||
		processedContent.includes('<industry-option') ||
		processedContent.includes('<dd-preview') ||
		processedContent.includes('<dd-options') ||
		processedContent.includes('<dd-add-button');

	// Don't render until contexts are ready to avoid showing raw HTML
	if (!contextsReady) {
		if (hasCustomTags) {
			console.warn('⚠️ Contexts not ready, waiting to render custom tags');
		}
		return <div ref={contentRef} style={{ fontSize: '14px' }} />;
	}

	return (
		<div ref={contentRef} style={{ fontSize: '14px' }}>
			<ReactMarkdown
				key={markdownKey}
				remarkPlugins={[remarkGfm]}
				rehypePlugins={[rehypeRaw]}
				components={components as any}
			>
				{processedContent}
			</ReactMarkdown>
		</div>
	);
};

const LazyImage: FC<{
	src: string;
	alt?: string;
	onImageClick?: (src: string) => void;
}> = memo(({ src, alt, onImageClick }) => {
	const [isLoaded, setIsLoaded] = useState(loadedImagesCache.has(src));

	useEffect(() => {
		if (isLoaded) {
			loadedImagesCache.add(src);
		}
	}, [isLoaded, src]);

	return (
		<Box
			position="relative"
			borderRadius="lg"
			overflow="hidden"
			cursor="pointer"
			maxW="200px"
			minW="200px"
		>
			{!isLoaded && <Skeleton h="150px" w="full" />}
			<Image
				src={src}
				alt={alt}
				onError={() => setIsLoaded(true)}
				onLoad={() => setIsLoaded(true)}
				onClick={onImageClick ? () => onImageClick(src) : undefined}
				display={isLoaded ? 'block' : 'none'}
				borderRadius="lg"
				objectFit="cover"
				transition="opacity 0.3s ease-in-out"
			/>
		</Box>
	);
});

LazyImage.displayName = 'LazyImage';

// Separate component for location preview to use context properly
const LocationPreviewComponent: FC<{
	locationId: number;
}> = ({ locationId }) => {
	const {
		locations,
		setEditingLocation,
		setLocationModalOpen,
		removeLocation,
		isLocationRemoved,
	} = useChatData();

	// Delete confirmation modal state
	const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
		useState(false);
	const [locationToDelete, setLocationToDelete] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [isDeletingLocation, setIsDeletingLocation] = useState(false);

	// Get location from context by index
	const displayLocation = locations[locationId];
	if (!displayLocation) {
		// Location was removed or doesn't exist at this index - don't render
		return null;
	}

	const itemLocationId = displayLocation.id || displayLocation.name;
	const isRemoved = isLocationRemoved(itemLocationId);

	// If the location was marked as removed, don't render
	if (isRemoved) {
		return null;
	}

	const address = {
		street: displayLocation.address?.street || '',
		city: displayLocation.address?.city || '',
		state: displayLocation.address?.state || '',
		postalCode: displayLocation.address?.postalCode || '',
		country: displayLocation.address?.country || 'USA',
		lat: displayLocation.address?.lat || 40.7128,
		long: displayLocation.address?.long || -74.006,
		placeId: displayLocation.address?.placeId,
	};

	const handleEdit = () => {
		setEditingLocation(displayLocation);
		setLocationModalOpen(true);
	};

	const handleRemove = () => {
		const locationId = displayLocation.id || displayLocation.name;
		setLocationToDelete({ id: locationId, name: displayLocation.name });
		setIsConfirmDeleteModalOpen(true);
	};

	const confirmDeleteLocation = async () => {
		if (!locationToDelete) return;

		setIsDeletingLocation(true);
		try {
			removeLocation(locationToDelete.id);
			setIsConfirmDeleteModalOpen(false);
			setLocationToDelete(null);
		} catch (error) {
			console.error('Error removing location:', error);
		} finally {
			setIsDeletingLocation(false);
		}
	};

	return (
		<>
			<div style={{ display: isRemoved ? 'none' : 'block', width: '100%', maxWidth: '600px', marginBottom: '16px' }}>
				<LocationCard
					title={displayLocation.name}
					address={address}
					type="location"
					height="300px"
					maxWidth="600px"
					onEdit={handleEdit}
					onRemove={handleRemove}
				/>
			</div>

			{/* Delete Location Confirmation Modal */}
			<Modal
				isOpen={isConfirmDeleteModalOpen}
				onClose={() => {
					setIsConfirmDeleteModalOpen(false);
					setLocationToDelete(null);
				}}
				isCentered
			>
				<ModalOverlay zIndex={20000} />
				<ModalContent
					containerProps={{
						zIndex: 20001,
					}}
				>
					<ModalHeader>Confirm Deletion</ModalHeader>
					<ModalBody>
						<Text>
							Are you sure you want to delete the location{' '}
							<Text as="span" fontWeight="bold">
								"{locationToDelete?.name}"
							</Text>
							?
						</Text>
					</ModalBody>
					<ModalFooter>
						<Flex w="full" justifyContent="flex-end" gap={2}>
							<Button
								onClick={() => {
									setIsConfirmDeleteModalOpen(false);
									setLocationToDelete(null);
								}}
								isDisabled={isDeletingLocation}
							>
								Cancel
							</Button>
							<Button
								onClick={confirmDeleteLocation}
								colorScheme="secondary"
								isLoading={isDeletingLocation}
								loadingText="Deleting..."
							>
								Delete
							</Button>
						</Flex>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

// Separate component for service area preview to use context properly
const ServiceAreaPreviewComponent: FC<{
	serviceAreaId: number;
}> = ({ serviceAreaId }) => {
	const {
		serviceAreas,
		setEditingServiceArea,
		setServiceAreaModalOpen,
		removeServiceArea,
		isServiceAreaRemoved,
	} = useChatData();

	// Delete confirmation modal state
	const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
		useState(false);
	const [serviceAreaToDelete, setServiceAreaToDelete] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [isDeletingServiceArea, setIsDeletingServiceArea] = useState(false);

	// Geocoded coordinates for geographic service areas
	const [geocodedCoords, setGeocodedCoords] = useState<{ lat: number; lng: number } | null>(null);
	const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);

	// Get service area from context by index
	const displayServiceArea = serviceAreas[serviceAreaId];

	// Load Google Maps API
	useEffect(() => {
		const loadGoogleMaps = () => {
			// Check if API key is configured
			if (!config.google?.mapsApiKey) {
				console.log('[ServiceAreaPreview] Google Maps API key not configured');
				return;
			}

			// Check if already loaded
			if (window.google && window.google.maps && window.google.maps.Geocoder) {
				console.log('[ServiceAreaPreview] Google Maps already loaded');
				setIsGoogleMapsLoaded(true);
				return;
			}

			// Check if script is already loading
			const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
			if (existingScript) {
				// Script is loading, wait for it to be ready
				let retryCount = 0;
				const maxRetries = 50; // 5 seconds max
				const checkLoaded = () => {
					if (window.google && window.google.maps && window.google.maps.Geocoder) {
						console.log('[ServiceAreaPreview] Google Maps finished loading (waited)');
						setIsGoogleMapsLoaded(true);
					} else if (retryCount < maxRetries) {
						retryCount++;
						setTimeout(checkLoaded, 100);
					} else {
						console.log('[ServiceAreaPreview] Google Maps loading timeout');
					}
				};
				checkLoaded();
				return;
			}

			// Create and load script
			console.log('[ServiceAreaPreview] Loading Google Maps script');
			const callbackName = `initGoogleMapsForServiceArea_${Date.now()}`;
			(window as any)[callbackName] = () => {
				console.log('[ServiceAreaPreview] Google Maps callback fired');
				// Wait a bit for Geocoder to be available
				let retryCount = 0;
				const checkApiReady = () => {
					if (window.google && window.google.maps && window.google.maps.Geocoder) {
						console.log('[ServiceAreaPreview] Google Maps fully loaded');
						setIsGoogleMapsLoaded(true);
					} else if (retryCount < 50) {
						retryCount++;
						setTimeout(checkApiReady, 100);
					}
				};
				checkApiReady();
			};

			const script = document.createElement('script');
			script.src = `https://maps.googleapis.com/maps/api/js?key=${config.google.mapsApiKey}&libraries=places&loading=async&callback=${callbackName}`;
			script.async = true;
			script.defer = true;
			script.onerror = () => {
				console.error('[ServiceAreaPreview] Failed to load Google Maps API');
			};
			document.head.appendChild(script);
		};

		loadGoogleMaps();
	}, []);

	// Geocode geographic service areas that don't have coordinates
	useEffect(() => {
		console.log('[ServiceAreaPreview] Geocode useEffect triggered', {
			hasDisplayServiceArea: !!displayServiceArea,
			name: displayServiceArea?.name,
			type: displayServiceArea?.type,
			geocodedCoords,
			isGoogleMapsLoaded,
		});

		if (!displayServiceArea) {
			console.log('[ServiceAreaPreview] No displayServiceArea, returning');
			return;
		}

		// Skip if already geocoded
		if (geocodedCoords) {
			console.log('[ServiceAreaPreview] Already geocoded, skipping');
			return;
		}

		// Skip if Google Maps not loaded yet
		if (!isGoogleMapsLoaded) {
			console.log('[ServiceAreaPreview] Google Maps not loaded yet');
			return;
		}

		// Check if it's a geographic service area without coordinates
		// Use type field if set, otherwise infer from presence of geographic object
		const isGeographic = displayServiceArea.type === 'geographic' ||
			(!displayServiceArea.type && displayServiceArea.geographic);
		const hasNoCoords = !displayServiceArea.address?.lat && !displayServiceArea.address?.long &&
			!displayServiceArea.geographic?.lat && !displayServiceArea.geographic?.lng;

		console.log('[ServiceAreaPreview] Conditions:', {
			isGeographic,
			hasNoCoords,
			originalType: displayServiceArea.type,
			hasGeographicObj: !!displayServiceArea.geographic,
		});

		if (isGeographic && hasNoCoords) {
			// Build search query from geographic entity
			const searchQuery = displayServiceArea.geographic?.displayName ||
				displayServiceArea.geographic?.entityName ||
				displayServiceArea.name;
			console.log('[ServiceAreaPreview] Search query:', searchQuery);
			if (!searchQuery) return;

			const country = displayServiceArea.geographic?.country || 'US';
			const fullQuery = `${searchQuery}, ${country}`;

			console.log('[ServiceAreaPreview] Geocoding:', fullQuery);

			const geocoder = new window.google.maps.Geocoder();
			geocoder.geocode({ address: fullQuery }, (results: any, status: any) => {
				console.log('[ServiceAreaPreview] Geocode response:', { status, resultsCount: results?.length });
				if (status === 'OK' && results && results[0]) {
					const location = results[0].geometry.location;
					console.log('[ServiceAreaPreview] Geocoded result:', fullQuery, location.lat(), location.lng());
					setGeocodedCoords({
						lat: location.lat(),
						lng: location.lng(),
					});
				} else {
					console.log('[ServiceAreaPreview] Geocoding failed:', fullQuery, status);
				}
			});
		} else {
			console.log('[ServiceAreaPreview] Not geocoding - isGeographic:', isGeographic, 'hasNoCoords:', hasNoCoords);
		}
	}, [displayServiceArea, geocodedCoords, isGoogleMapsLoaded]);

	if (!displayServiceArea) {
		// Service area was removed or doesn't exist at this index - don't render
		return null;
	}

	const itemServiceAreaId = displayServiceArea.id || displayServiceArea.name;
	const isRemoved = isServiceAreaRemoved(itemServiceAreaId);

	// If the service area was marked as removed, don't render
	if (isRemoved) {
		return null;
	}

	// Determine coordinates - use geocoded for geographic, or from address, or default
	let lat: number | string = 40.7128; // NYC default
	let long: number | string = -74.006;

	if (displayServiceArea.type === 'geographic') {
		// For geographic service areas, use geocoded coords or geographic coords
		if (geocodedCoords) {
			lat = geocodedCoords.lat;
			long = geocodedCoords.lng;
		} else if (displayServiceArea.geographic?.lat && displayServiceArea.geographic?.lng) {
			lat = displayServiceArea.geographic.lat;
			long = displayServiceArea.geographic.lng;
		}
	} else if (displayServiceArea.address?.lat && displayServiceArea.address?.long) {
		// For radius service areas, use address coords
		lat = displayServiceArea.address.lat;
		long = displayServiceArea.address.long;
	}

	const address = {
		street: displayServiceArea.address?.street || '',
		city: displayServiceArea.address?.city || displayServiceArea.geographic?.entityName || '',
		state: displayServiceArea.address?.state || displayServiceArea.geographic?.state || '',
		postalCode: displayServiceArea.address?.postalCode || '',
		country: displayServiceArea.address?.country || displayServiceArea.geographic?.country || 'USA',
		lat,
		long,
	};

	const handleEdit = () => {
		console.log('🎯 [ServiceAreaPreview] Edit clicked:', {
			displayServiceArea,
			serviceAreaId,
			hasSetEditingServiceArea: !!setEditingServiceArea,
			hasSetServiceAreaModalOpen: !!setServiceAreaModalOpen
		});
		setEditingServiceArea(displayServiceArea);
		setServiceAreaModalOpen(true);
		console.log('🎯 [ServiceAreaPreview] Modal state updated');
	};

	const handleRemove = () => {
		const serviceAreaId = displayServiceArea.id || displayServiceArea.name;
		setServiceAreaToDelete({
			id: serviceAreaId,
			name: displayServiceArea.name,
		});
		setIsConfirmDeleteModalOpen(true);
	};

	const confirmDeleteServiceArea = async () => {
		if (!serviceAreaToDelete) return;

		setIsDeletingServiceArea(true);
		try {
			removeServiceArea(serviceAreaToDelete.id);
			setIsConfirmDeleteModalOpen(false);
			setServiceAreaToDelete(null);
		} catch (error) {
			console.error('Error removing service area:', error);
		} finally {
			setIsDeletingServiceArea(false);
		}
	};

	// Get entity type for geographic service areas (infer type if not set)
	const isGeoType = displayServiceArea.type === 'geographic' ||
		(!displayServiceArea.type && displayServiceArea.geographic);
	const entityType = isGeoType
		? displayServiceArea.geographic?.entityType
		: undefined;

	return (
		<>
			<div
				style={{
					display: isRemoved ? 'none' : 'block',
					width: '100%',
					minWidth: '336px',
					maxWidth: '480px',
					marginBottom: '16px',
				}}
			>
				<LocationCard
					key={`${displayServiceArea.name}-${address.lat}-${address.long}`}
					title={displayServiceArea.name}
					address={address}
					type="service-area"
					radius={!isGeoType && displayServiceArea.radius ? Number(displayServiceArea.radius) : undefined}
					entityType={entityType}
					height="300px"
					maxWidth="480px"
					onEdit={handleEdit}
					onRemove={handleRemove}
				/>
			</div>

			{/* Delete Service Area Confirmation Modal */}
			<Modal
				isOpen={isConfirmDeleteModalOpen}
				onClose={() => {
					setIsConfirmDeleteModalOpen(false);
					setServiceAreaToDelete(null);
				}}
				isCentered
			>
				<ModalOverlay zIndex={20000} />
				<ModalContent
					containerProps={{
						zIndex: 20001,
					}}
				>
					<ModalHeader>Confirm Deletion</ModalHeader>
					<ModalBody>
						<Text>
							Are you sure you want to delete the service area{' '}
							<Text as="span" fontWeight="bold">
								"{serviceAreaToDelete?.name}"
							</Text>
							?
						</Text>
					</ModalBody>
					<ModalFooter>
						<Flex w="full" justifyContent="flex-end" gap={2}>
							<Button
								onClick={() => {
									setIsConfirmDeleteModalOpen(false);
									setServiceAreaToDelete(null);
								}}
								isDisabled={isDeletingServiceArea}
							>
								Cancel
							</Button>
							<Button
								onClick={confirmDeleteServiceArea}
								colorScheme="secondary"
								isLoading={isDeletingServiceArea}
								loadingText="Deleting..."
							>
								Delete
							</Button>
						</Flex>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

// Separate component for customer profile card to use context properly
const CustomerProfileCardComponent: FC<{
	profileIndex: number;
}> = ({ profileIndex }) => {
	const { customerProfiles: chatCustomerProfiles } = useChatData();
	const {
		setEditingCustomerProfile,
		setCustomerProfileModalOpen,
		removeCustomerProfile,
	} = useCopilotChatContext();

	// Monitor global update timestamp to force re-render when profile is updated
	const [updateTimestamp, setUpdateTimestamp] = React.useState(Date.now());

	// Delete confirmation modal state (local to this component)
	const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
	const [profileToDelete, setProfileToDelete] = useState<any>(null);

	React.useEffect(() => {
		const checkForUpdates = () => {
			const globalTimestamp = (window as any).__customerProfileUpdateTimestamp;
			if (globalTimestamp && globalTimestamp > updateTimestamp) {
				setUpdateTimestamp(globalTimestamp);
			}
		};

		const interval = setInterval(checkForUpdates, 100);
		return () => clearInterval(interval);
	}, [updateTimestamp]);

	// Get profile from chat context by index
	const profile = chatCustomerProfiles?.[profileIndex];

	if (!profile) {
		return null;
	}

	const handleEdit = async () => {
		console.log('🎯🎯🎯 [handleEdit] START - Edit button clicked');
		console.log('🎯 [handleEdit] Profile from context:', JSON.stringify({
			_id: profile._id,
			name: profile.name,
			customer_rules: (profile as any).customer_rules,
			customer_tones: (profile as any).customer_tones,
			hasRules: !!(profile as any).customer_rules?.length,
			hasTones: !!(profile as any).customer_tones?.length,
		}, null, 2));
		try {
			let enrichedProfile = profile;

			// Always try to fetch the latest profile from API to ensure we have all data
			// (customer_rules, customer_tones, etc.)
			if (profile._id) {
				console.log('🎯 [handleEdit] Fetching profile from API with _id:', profile._id);
				try {
					const { getCustomerProfiles } = await import('src/services/account');
					const list = await getCustomerProfiles();
					console.log('🎯 [handleEdit] API returned', list.length, 'profiles');
					const found = list.find((p: any) => p._id === profile._id);
					if (found) {
						console.log('🎯 [handleEdit] Found in API:', JSON.stringify({
							_id: found._id,
							name: found.name,
							customer_rules: found.customer_rules,
							customer_tones: found.customer_tones,
							hasRules: !!found.customer_rules?.length,
							hasTones: !!found.customer_tones?.length,
						}, null, 2));
						// Merge API data with local profile data, preserving local fields that API doesn't return
						enrichedProfile = {
							...profile, // Start with local data (may have customer_rules, customer_tones from copilot)
							...found, // Override with API data
							// Ensure customer_rules and customer_tones are preserved from either source
							customer_rules: found.customer_rules?.length > 0
								? found.customer_rules
								: (profile as any).customer_rules || [],
							customer_tones: found.customer_tones?.length > 0
								? found.customer_tones
								: (profile as any).customer_tones || [],
						} as any;
						console.log('🎯 [handleEdit] After merge - enrichedProfile:', JSON.stringify({
							_id: (enrichedProfile as any)._id,
							name: (enrichedProfile as any).name,
							customer_rules: (enrichedProfile as any).customer_rules,
							customer_tones: (enrichedProfile as any).customer_tones,
							hasRules: !!(enrichedProfile as any).customer_rules?.length,
							hasTones: !!(enrichedProfile as any).customer_tones?.length,
						}, null, 2));
					} else {
						console.warn('🎯 [handleEdit] Profile NOT found in API, using local data only');
						console.log('🎯 [handleEdit] Local profile has rules:', !!(profile as any).customer_rules?.length);
						console.log('🎯 [handleEdit] Local profile has tones:', !!(profile as any).customer_tones?.length);
					}
				} catch (e) {
					// ignore fetch errors, continue with current profile
					console.error('🎯 [handleEdit] API fetch error:', e);
				}
			} else {
				console.log('🎯 [handleEdit] No _id on profile, using local data only');
			}

			console.log('🎯 [handleEdit] FINAL enrichedProfile being sent to modal:', JSON.stringify({
				_id: (enrichedProfile as any)._id,
				name: (enrichedProfile as any).name,
				customer_rules: (enrichedProfile as any).customer_rules,
				customer_tones: (enrichedProfile as any).customer_tones,
			}, null, 2));

			// Set editing profile and open modal via context
			setEditingCustomerProfile(enrichedProfile as any);
			setCustomerProfileModalOpen(true);
			console.log('🎯🎯🎯 [handleEdit] END - Modal should now be open');
		} catch (e) {
			console.error('Failed to open CustomerProfileFormModal:', e);
		}
	};

	const handleDelete = () => {
		setProfileToDelete(profile);
		setIsConfirmDeleteModalOpen(true);
	};

	const confirmDelete = async () => {
		if (!profileToDelete) return;
		try {
			const { deleteCustomerProfile } = await import('src/services/account');
			await deleteCustomerProfile(profileToDelete._id);
			// Remove from context state
			removeCustomerProfile(profileToDelete._id);
			setIsConfirmDeleteModalOpen(false);
			setProfileToDelete(null);
		} catch (e) {
			console.error('Failed to delete customer profile:', e);
		}
	};

	return (
		<>
			<Box w="100%" maxW="500px" mb={3}>
				<CustomerProfileCard
					customerProfile={profile}
					onEdit={handleEdit}
					onDelete={handleDelete}
					showActions
					actionType="delete"
				/>
			</Box>

			{/* Delete Confirmation Modal */}
			<Modal
				isOpen={isConfirmDeleteModalOpen}
				onClose={() => {
					setIsConfirmDeleteModalOpen(false);
					setProfileToDelete(null);
				}}
				isCentered
			>
				<ModalOverlay zIndex={20000} />
				<ModalContent containerProps={{ zIndex: 20001 }}>
					<ModalHeader>Confirm Deletion</ModalHeader>
					<ModalBody>
						<Text>
							Are you sure you want to delete the customer profile{' '}
							<Text as="span" fontWeight="bold">
								"{profileToDelete?.name}"
							</Text>
							?
						</Text>
					</ModalBody>
					<ModalFooter>
						<Flex w="full" justifyContent="flex-end" gap={2}>
							<Button
								onClick={() => {
									setIsConfirmDeleteModalOpen(false);
									setProfileToDelete(null);
								}}
							>
								Cancel
							</Button>
							<Button onClick={confirmDelete} colorScheme="secondary">
								Delete
							</Button>
						</Flex>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

export default Message;
